import type { AccessRole, AdminUser, FeedbackRow, QuestionWiseReport, RegistrationSummary, SurveyQuestion, TeamMember } from '@ferty9/types';
import { MANAGEMENT_CENTER } from '@ferty9/constants';
import { ChoicesSelect } from '../components/ChoicesSelect';
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import type { AdminActions } from '../types';
import { countryPhoneCodes, digitsOnly, normalizePhoneNumber, parsePhoneNumber } from '../lib/phone';
import { cleanVerifiedMobileSuffix, formatDate, questionWiseRows } from '../utils';
import { AdminPdfActionIcon, adminPdfActionButtonClassName, adminPdfActionButtonStyle, SelectGroup } from './common';

type Row = Record<string, unknown>;

function SmallIcon({ name }: { name: 'message' | 'refresh' | 'download' | 'search' | 'save' | 'user' | 'person' | 'shield' | 'lock' | 'fileEdit' | 'location' | 'edit' | 'trash' | 'map' | 'zap' | 'link' | 'arrowDown' | 'chevronRight' | 'smile' | 'meh' | 'frown' | 'bar' | 'check' | 'close' }) {
  const common = { xmlns: 'http://www.w3.org/2000/svg', width: 16, height: 16, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2.5, strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const };
  if (name === 'message') return <svg {...common} width={20} height={20}><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" /></svg>;
  if (name === 'download') return <svg {...common}><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" /></svg>;
  if (name === 'search') return <svg {...common}><circle cx="11" cy="11" r="8" /><path d="m21 21-4.3-4.3" /></svg>;
  if (name === 'save') return <svg {...common} width={14} height={14}><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" /><polyline points="17 21 17 13 7 13 7 21" /><polyline points="7 3 7 8 15 8" /></svg>;
  if (name === 'user') return <svg {...common}><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><line x1="19" y1="8" x2="19" y2="14" /><line x1="22" y1="11" x2="16" y2="11" /></svg>;
  if (name === 'person') return <svg {...common} width={15} height={15}><path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" /></svg>;
  if (name === 'shield') return <svg {...common} width={15} height={15}><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" /></svg>;
  if (name === 'lock') return <svg {...common} width={12} height={12}><rect x="3" y="11" width="18" height="11" rx="2" ry="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" /></svg>;
  if (name === 'fileEdit') return <svg {...common} width={12} height={12}><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" /><path d="M18.5 2.5a2.121 2.121 0 1 1 3 3L12 15l-4 1 1-4 9.5-9.5z" /></svg>;
  if (name === 'location') return <svg {...common} width={13} height={13}><path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z" /><circle cx="12" cy="10" r="3" /></svg>;
  if (name === 'edit') return <svg {...common} width={12} height={12}><path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z" /><path d="m15 5 4 4" /></svg>;
  if (name === 'trash') return <svg {...common} width={12} height={12}><path d="M3 6h18" /><path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6" /><path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2" /><line x1="10" y1="11" x2="10" y2="17" /><line x1="14" y1="11" x2="14" y2="17" /></svg>;
  if (name === 'zap') return <svg {...common} width={13} height={13}><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" /></svg>;
  if (name === 'link') return <svg {...common} width={13} height={13}><path d="M15 7h3a5 5 0 0 1 0 10h-3" /><path d="M9 17H6A5 5 0 0 1 6 7h3" /><line x1="8" y1="12" x2="16" y2="12" /></svg>;
  if (name === 'arrowDown') return <svg {...common} width={13} height={13}><line x1="12" y1="5" x2="12" y2="19" /><polyline points="19 12 12 19 5 12" /></svg>;
  if (name === 'chevronRight') return <svg {...common} width={20} height={20} strokeWidth={3}><path d="m9 18 6-6-6-6" /></svg>;
  if (name === 'map') return <svg {...common}><polygon points="3 6 9 3 15 6 21 3 21 18 15 21 9 18 3 21 3 6" /><line x1="9" y1="3" x2="9" y2="18" /><line x1="15" y1="6" x2="15" y2="21" /></svg>;
  if (name === 'smile') return <svg {...common} width={14} height={14}><circle cx="12" cy="12" r="10" /><path d="M8 14s1.5 2 4 2 4-2 4-2" /><line x1="9" y1="9" x2="9.01" y2="9" /><line x1="15" y1="9" x2="15.01" y2="9" /></svg>;
  if (name === 'meh') return <svg {...common} width={14} height={14}><circle cx="12" cy="12" r="10" /><line x1="8" y1="12" x2="16" y2="12" /><line x1="9" y1="9" x2="9.01" y2="9" /><line x1="15" y1="9" x2="15.01" y2="9" /></svg>;
  if (name === 'frown') return <svg {...common} width={14} height={14}><circle cx="12" cy="12" r="10" /><path d="M16 16s-1.5-2-4-2-4 2-4 2" /><line x1="9" y1="9" x2="9.01" y2="9" /><line x1="15" y1="9" x2="15.01" y2="9" /></svg>;
  if (name === 'bar') return <svg {...common} width={12} height={12}><line x1="18" y1="20" x2="18" y2="10" /><line x1="12" y1="20" x2="12" y2="4" /><line x1="6" y1="20" x2="6" y2="14" /></svg>;
  if (name === 'check') return <svg {...common} width={12} height={12}><path d="M20 6 9 17l-5-5" /></svg>;
  if (name === 'close') return <svg {...common} width={12} height={12}><path d="M18 6 6 18" /><path d="m6 6 12 12" /></svg>;
  return <svg {...common}><path d="M21 12a9 9 0 0 0-9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" /><path d="M3 3v5h5" /><path d="M3 12a9 9 0 0 0 9 9 9.75 9.75 0 0 0 6.74-2.74L21 16" /><path d="M16 16h5v5" /></svg>;
}

function npsBadgeColor(score: unknown) {
  const value = Number(score);
  if (value >= 9) return '#10b981';
  if (value >= 7) return '#f59e0b';
  return '#ef4444';
}

function tabHeader(title: string, button?: React.ReactNode) {
  return <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 32, borderBottom: '1px solid var(--border)', paddingBottom: 20, gap: 16, flexWrap: 'wrap' }}><h2 className="section-title" style={{ margin: 0, fontFamily: 'Outfit', fontSize: 28, fontWeight: 800, color: 'var(--text)' }}>{title}</h2>{button}</div>;
}

function cell(value: unknown) {
  if (value == null || value === '') return '-';
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

function uniqueOptions(values: readonly string[]) {
  return [...new Set(values.map(value => value.trim()).filter(Boolean))];
}

function PhoneNumberField({
  id,
  label,
  value,
  onChange,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
}) {
  const parsed = parsePhoneNumber(value);
  const [countryIso, setCountryIso] = useState(parsed.iso);
  const [localNumber, setLocalNumber] = useState(parsed.localNumber);

  useEffect(() => {
    const next = parsePhoneNumber(value);
    setCountryIso(next.iso);
    setLocalNumber(next.localNumber);
  }, [value]);

  const update = (nextCountryIso: string, nextLocalNumber: string) => {
    const selectedCountry = countryPhoneCodes.find(country => country.iso === nextCountryIso) || countryPhoneCodes[0];
    setCountryIso(selectedCountry.iso);
    setLocalNumber(nextLocalNumber);
    onChange(normalizePhoneNumber(selectedCountry.dialCode, nextLocalNumber));
  };

  return (
    <div className="input-group phone-input-group">
      <label>{label}</label>
      <div className="phone-input-row">
        <div className="phone-country-select">
          <ChoicesSelect
            id={`${id}-country`}
            value={countryIso}
            searchEnabled
            options={countryPhoneCodes.map(country => ({
              label: `${country.flag} ${country.name} (+${country.dialCode})`,
              value: country.iso,
            }))}
            onChange={nextCountryIso => update(nextCountryIso, localNumber)}
          />
        </div>
        <input
          id={id}
          inputMode="numeric"
          value={localNumber}
          onChange={event => update(countryIso, digitsOnly(event.target.value))}
          placeholder="Phone number"
        />
      </div>
    </div>
  );
}

function roleLabel(role: unknown, isSuperAdmin?: unknown) {
  if (isSuperAdmin || role === 'SUPERADMIN') return 'Superadmin';
  if (role === 'ADMIN') return 'Administration';
  if (role === 'REPORTS') return 'Dashboard and Reports';
  if (role === 'SURVEY') return 'Survey Form Entry';
  return formatRoleKey(role);
}

function normalizeRoleKeyForUi(role: unknown) {
  return String(role || '').trim().toUpperCase().replace(/[^A-Z0-9]+/g, '_');
}

function isProtectedLogoutRole(role: unknown) {
  const value = normalizeRoleKeyForUi(role);
  return value === 'SUPERADMIN' || value === 'ADMIN';
}

function isProtectedUserForLogout(user: AdminUser) {
  if (user.is_superadmin || user.isRootAdmin) return true;
  const roles = [...(user.roles || []), user.role];
  return roles.some(isProtectedLogoutRole);
}

function userMatchesSearch(user: AdminUser, search: string) {
  const fields = [
    user.username,
    user.first_name,
    user.middle_name,
    user.last_name,
    user.display_name,
    user.email,
    user.phone_number,
    user.center_name,
    user.role,
    ...(user.roles || []),
    ...(user.centerAccess || []),
  ];
  return fields.some(value => String(value || '').toLowerCase().includes(search));
}

function formatRoleKey(role: unknown) {
  const value = String(role || '').trim();
  if (!value) return 'Dashboard and Reports';
  return value
    .toLowerCase()
    .split(/[_\s-]+/)
    .filter(Boolean)
    .map(part => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

function journeyLabel(value: unknown) {
  if (value === 'NEW_PATIENT') return 'New Patient';
  if (value === 'ET') return 'Day of ET';
  return cell(value);
}

function summaryMetricCells(value: unknown) {
  const row = value && typeof value === 'object' ? value as Row : {};
  const patientCount = Number(row.patientCount ?? row.patient_count ?? row.registrations ?? row.totalRegistrations ?? row.count ?? 0);
  const feedbackCount = Number(row.feedbackCount ?? row.feedback_count ?? row.feedbacks ?? row.responses ?? 0);
  const collectionPct = patientCount > 0 ? Math.round((feedbackCount / patientCount) * 100) : 0;
  return [
    patientCount,
    feedbackCount,
    `${collectionPct}%`,
    cell(row.nps ?? row.npsScore ?? 0),
  ];
}

function summaryMetricNumbers(value: unknown) {
  const row = value && typeof value === 'object' ? value as Row : {};
  return {
    patient: Number(row.patientCount ?? row.patient_count ?? row.registrations ?? row.totalRegistrations ?? row.count ?? 0),
    feedback: Number(row.feedbackCount ?? row.feedback_count ?? row.feedbacks ?? row.responses ?? 0),
    nps: Number(row.nps ?? row.npsScore ?? 0),
  };
}

type FeedbackPaginationView = {
  page: number;
  limit: string;
  pageSize: number;
  totalRows: number;
  totalPages: number;
  hasPreviousPage: boolean;
  hasNextPage: boolean;
};

const feedbackPageSizeOptions = ['50', '60', '70', '80', '90', '100', '200', '300', '400', '500', 'ALL'];

export function FeedbackResponsesTab({
  active,
  rows,
  pagination,
  centers,
  isAdmin,
  canExport,
  feedbackCenter,
  journey,
  bucket,
  dateRange,
  customFromDate,
  customToDate,
  search,
  pageSize,
  actions,
}: {
  active: boolean;
  rows: FeedbackRow[];
  pagination: FeedbackPaginationView;
  centers: string[];
  isAdmin: boolean;
  canExport: boolean;
  feedbackCenter: string;
  journey: string;
  bucket: string;
  dateRange: string;
  customFromDate: string;
  customToDate: string;
  search: string;
  pageSize: string;
  actions: AdminActions;
}) {
  const [exportModalOpen, setExportModalOpen] = useState(false);
  const startIndex = pagination.limit === 'ALL' ? 0 : (pagination.page - 1) * pagination.pageSize;
  const pageSummary = pagination.limit === 'ALL'
    ? `Showing all ${pagination.totalRows} responses`
    : `Page ${pagination.page} of ${pagination.totalPages} • Showing ${rows.length} of ${pagination.totalRows}`;

  const handleExport = (scope: 'current-page' | 'full-result') => {
    setExportModalOpen(false);
    void actions.exportFeedback(scope);
  };

  return (
    <section id="tab-feedback" className="tab-content" style={{ display: active ? 'block' : 'none' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24, flexWrap: 'wrap', gap: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={{ color: 'var(--primary)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', background: 'var(--primary-light)', width: 40, height: 40, borderRadius: 12 }}><SmallIcon name="message" /></span>
          <h3 style={{ margin: 0, fontFamily: 'Outfit, sans-serif', fontSize: 20, fontWeight: 800, color: 'var(--text)' }}>Patient Feedback Responses</h3>
        </div>
        <span id="feedback-count-badge" className="badge" style={{ background: 'var(--primary-light)', color: 'var(--primary)', fontWeight: 800, fontSize: 11, padding: '6px 14px', borderRadius: 10, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Total Responses: {pagination.totalRows}</span>
      </div>
      <div className="card" style={{ border: '1px solid var(--border)', boxShadow: 'var(--shadow-md)', padding: 24, position: 'relative', zIndex: 10 }}>
        <div className="filter-row feedback-filter-row">
          <SelectGroup label="Date Range" id="filter-feedback-date-range" value={dateRange} options={['ALL', 'CURRENT_MONTH', 'PREVIOUS_MONTH', 'LAST_3_MONTHS', 'CURRENT_WEEK', 'LAST_WEEK', 'YTD', 'FY_YTD', 'CUSTOM', '7', '30', '90', '365']} labels={{ ALL: 'All Time', CURRENT_MONTH: 'Current Month', PREVIOUS_MONTH: 'Previous Month', LAST_3_MONTHS: 'Last 3 Months', CURRENT_WEEK: 'Current Week', LAST_WEEK: 'Last Week', YTD: 'YTD (Year to Date)', FY_YTD: 'FY-YTD (Financial Year to Date)', CUSTOM: 'Custom Date', 7: 'Last 7 Days', 30: 'Last 30 Days', 90: 'Last 90 Days', 365: 'Last Year' }} onChange={actions.setFeedbackDateRange} />
          {dateRange === 'CUSTOM' && (
            <>
              <div className="filter-group">
                <label htmlFor="filter-feedback-from">From</label>
                <input id="filter-feedback-from" type="date" value={customFromDate} onChange={event => actions.setFeedbackCustomFromDate(event.target.value)} />
              </div>
              <div className="filter-group">
                <label htmlFor="filter-feedback-to">To</label>
                <input id="filter-feedback-to" type="date" value={customToDate} onChange={event => actions.setFeedbackCustomToDate(event.target.value)} />
              </div>
            </>
          )}
          <SelectGroup label="Feedback Center" id="filter-feedback-center" value={feedbackCenter} options={['ALL', ...centers]} allLabel="All Centers" onChange={actions.setCenterFilter} />
          <SelectGroup label="Patient Journey" id="filter-journey" value={journey} options={['ALL', 'NEW_PATIENT', 'OPU', 'ET']} labels={{ ALL: 'All Journeys', NEW_PATIENT: 'New Patient', OPU: 'OPU', ET: 'Day of ET' }} onChange={actions.setJourneyFilter} />
          <SelectGroup label="NPS Category" id="filter-bucket" value={bucket} options={['ALL', 'PROMOTER', 'PASSIVE', 'DETRACTOR']} labels={{ ALL: 'All Responses', PROMOTER: 'Promoters (9-10)', PASSIVE: 'Passives (7-8)', DETRACTOR: 'Detractors (0-6)' }} onChange={actions.setBucketFilter} />
          <div className="filter-group">
            <label htmlFor="filter-feedback-search">Global Search</label>
            <div style={{ position: 'relative' }}>
              <span style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)', display: 'inline-flex' }}><SmallIcon name="search" /></span>
              <input id="filter-feedback-search" value={search} onChange={event => actions.setFeedbackSearch(event.target.value)} placeholder="Patient, MRN, staff, appointment..." style={{ paddingLeft: 40 }} />
            </div>
          </div>
          <SelectGroup label="Rows Per Page" id="filter-feedback-page-size" value={pageSize} options={feedbackPageSizeOptions} labels={{ ALL: 'All' }} onChange={actions.setFeedbackPageSize} />
          <button className="btn btn-primary report-action-btn" onClick={actions.refreshFeedback}><SmallIcon name="refresh" />Refresh</button>
          <button className="btn btn-outline report-action-btn" disabled={!canExport} title={!canExport ? 'Feedback Responses export permission is required' : undefined} onClick={() => setExportModalOpen(true)}><SmallIcon name="download" />Export Excel</button>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap', margin: '4px 0 16px' }}>
          <span style={{ color: 'var(--text-muted)', fontSize: 12, fontWeight: 800 }}>{pageSummary}</span>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <button className="btn btn-outline" disabled={!pagination.hasPreviousPage} onClick={() => actions.setFeedbackPage(Math.max(1, pagination.page - 1))} style={{ height: 38, padding: '0 16px', opacity: pagination.hasPreviousPage ? 1 : 0.5 }}>Previous</button>
            <button className="btn btn-outline" disabled={!pagination.hasNextPage} onClick={() => actions.setFeedbackPage(pagination.page + 1)} style={{ height: 38, padding: '0 16px', opacity: pagination.hasNextPage ? 1 : 0.5 }}>Next</button>
          </div>
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table id="feedback-table"><thead><tr><th style={{ width: 60, textAlign: 'center' }}>S.No</th><th>Date</th><th>Appointment ID</th><th>Patient Name</th><th>Journey</th><th>Feedback Center</th><th>Verified Number</th><th>NPS</th><th>Overall Feedback</th><th>Staff Member</th><th id="actions-header" style={{ display: isAdmin ? 'table-cell' : 'none' }}>Actions</th></tr></thead>
          <tbody id="feedback-body">{rows.length ? rows.map((row, index) => <tr key={String(row.id || index)}><td style={{ fontWeight: 700, color: 'var(--text-muted)', textAlign: 'center' }}>{startIndex + index + 1}</td><td>{formatDate(row.created_at)}</td><td>{cell(row.appointment_id)}</td><td><strong>{cell(row.patient_name)}</strong></td><td><span className="badge" style={{ background: '#f1f5f9', color: 'var(--text-muted)' }}>{journeyLabel(row.journey)}</span></td><td>{cell(row.center || row.registered_center)}</td><td>{cell(row.verified_mobile)}</td><td><span className="badge" style={{ background: npsBadgeColor(row.nps_score), color: 'white' }}>{cell(row.nps_score)}</span></td><td style={{ maxWidth: 300, fontSize: 12, whiteSpace: 'normal', wordBreak: 'break-word' }}>{cleanVerifiedMobileSuffix(row.comments)}</td><td>{cell(row.feedback_by)}</td><td className="action-cell" style={{ display: isAdmin ? 'table-cell' : 'none' }}>{isAdmin && <button className="btn" style={{ padding: '6px 12px', background: 'var(--danger)', color: 'white', borderRadius: 8, fontSize: 11 }} onClick={() => void actions.deleteFeedback(row.id)}>Delete</button>}</td></tr>) : <tr><td colSpan={11} style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)' }}>No responses found for selected filters.</td></tr>}</tbody></table>
        </div>
      </div>
      {exportModalOpen && (
        <div className="modal-overlay feedback-export-choice-overlay" onMouseDown={() => setExportModalOpen(false)}>
          <div className="modal-content admin-premium-modal feedback-export-choice-modal" onMouseDown={event => event.stopPropagation()}>
            <button onClick={() => setExportModalOpen(false)} aria-label="Close export options" className="admin-modal-close">&times;</button>
            <div className="feedback-export-choice-icon"><SmallIcon name="download" /></div>
            <h3>Export Feedback Responses</h3>
            <p>Select whether to export only the visible rows on this page or the full result matching the active filters.</p>
            <div className="feedback-export-choice-actions">
              <button className="btn btn-outline" onClick={() => handleExport('current-page')}>Download Current Page</button>
              <button className="btn btn-primary" onClick={() => handleExport('full-result')}>Download Full Result</button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

export function QuestionWiseTab({ active, report, centers, center, days, customFromDate, customToDate, journey, canExport, actions }: { active: boolean; report: QuestionWiseReport | null; centers: string[]; center: string; days: string; customFromDate: string; customToDate: string; journey: string; canExport: boolean; actions: AdminActions }) {
  const reportRecord = (report || {}) as Row;
  const rows = questionWiseRows(report) as Row[];
  const npsData = (reportRecord.npsData && typeof reportRecord.npsData === 'object' ? reportRecord.npsData : {}) as Row;
  return (
    <section id="tab-question-wise" className="tab-content" style={{ display: active ? 'block' : 'none' }}>
      <div className="card" style={{ marginBottom: 24, position: 'relative', zIndex: 10 }}>
        <div className="filter-row question-wise-filter-row">
          <SelectGroup label="Center Location" id="qw-filter-center" value={center} onChange={actions.setCenterFilter} options={['ALL', ...centers]} allLabel="All Centers" />
          <SelectGroup label="Patient Journey" id="qw-filter-journey" value={journey} onChange={actions.setJourneyFilter} options={['ALL', 'NEW_PATIENT', 'OPU', 'ET']} labels={{ ALL: 'All Journeys', NEW_PATIENT: 'New Patients: first visit', OPU: 'OPU', ET: 'Day of ET' }} />
          <SelectGroup label="Date Range" id="qw-filter-days" value={days} onChange={actions.setDaysFilter} options={['CURRENT_MONTH', 'PREVIOUS_MONTH', 'LAST_3_MONTHS', 'PREVIOUS_3_MONTHS', 'CURRENT_WEEK', 'LAST_WEEK', 'YTD', 'FY_YTD', 'CUSTOM', '7', '30', '90', '365', '9999']} labels={{ CURRENT_MONTH: 'Current Month', PREVIOUS_MONTH: 'Previous Month', LAST_3_MONTHS: 'Last 3 Months', PREVIOUS_3_MONTHS: 'Previous 3 Months', CURRENT_WEEK: 'Current Week', LAST_WEEK: 'Last Week', YTD: 'YTD (Year to Date)', FY_YTD: 'FY-YTD (Financial Year to Date)', CUSTOM: 'Custom Date', 7: 'Last 7 Days', 30: 'Last 30 Days', 90: 'Last 90 Days', 365: 'Last Year', 9999: 'All Time' }} />
          {days === 'CUSTOM' && <QuestionWiseCustomDateInputs from={customFromDate} to={customToDate} onFrom={actions.setCustomFromDate} onTo={actions.setCustomToDate} />}
          <button className="btn btn-primary report-action-btn" onClick={actions.refreshQuestionWise}><SmallIcon name="refresh" />Refresh</button>
          <button className="btn btn-outline report-action-btn" disabled={!canExport} title={!canExport ? 'Question Responses export permission is required' : undefined} onClick={() => void actions.exportQuestionWise()}><SmallIcon name="download" />Export Excel</button>
          <button className="btn btn-outline report-action-btn" disabled={!canExport} title={!canExport ? 'Question Responses export permission is required' : undefined} onClick={actions.printQuestionWise}><SmallIcon name="download" />Download PDF</button>
        </div>
      </div>
      <div id="qw-cards-container" style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
        <QuestionWiseContent rows={rows} npsData={npsData} canExport={canExport} actions={actions} />
      </div>
    </section>
  );
}

function QuestionWiseContent({ rows, npsData, canExport, actions }: { rows: Row[]; npsData: Row; canExport: boolean; actions: AdminActions }) {
  const total = Number(npsData.total || 0);
  if (!rows.length || total === 0) return <QuestionWiseEmpty onReset={() => { actions.setCenterFilter('ALL'); actions.setJourneyFilter('ALL'); actions.setDaysFilter('CURRENT_MONTH'); actions.setCustomFromDate(''); actions.setCustomToDate(''); actions.refreshQuestionWise(); }} />;
  const grouped = rows.reduce<Record<string, Row[]>>((acc, row) => {
    const category = cell(row.category || 'General');
    acc[category] ||= [];
    acc[category].push(row);
    return acc;
  }, {});
  return (
    <>
      <QuestionWiseStyles />
      <QuestionWiseOverview npsData={npsData} />
      {Object.entries(grouped).map(([category, items]) => <QuestionWiseSection key={category} category={category} rows={items} canExport={canExport} actions={actions} />)}
    </>
  );
}

function QuestionWiseCustomDateInputs({ from, to, onFrom, onTo }: { from: string; to: string; onFrom: (value: string) => void; onTo: (value: string) => void }) {
  return (
    <>
      <div className="input-group" style={{ minWidth: 170 }}>
        <label>From Date</label>
        <input type="date" value={from} onChange={event => onFrom(event.target.value)} style={{ height: 48 }} />
      </div>
      <div className="input-group" style={{ minWidth: 170 }}>
        <label>To Date</label>
        <input type="date" value={to} onChange={event => onTo(event.target.value)} style={{ height: 48 }} />
      </div>
    </>
  );
}

function QuestionWiseStyles() {
  return <style>{`
    .qw-comments-scroll::-webkit-scrollbar { width: 6px; }
    .qw-comments-scroll::-webkit-scrollbar-track { background: transparent; }
    .qw-comments-scroll::-webkit-scrollbar-thumb { background: var(--border); border-radius: 3px; }
    .qw-comments-scroll::-webkit-scrollbar-thumb:hover { background: var(--text-light); }
    .qw-comment-bubble:hover { transform: translateX(3px); border-left-color: var(--accent) !important; }
    .qw-detail-card { transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1); }
    .qw-detail-card:hover { transform: translateY(-4px); box-shadow: var(--shadow-lg) !important; border-color: rgba(233, 30, 99, 0.2) !important; }
    body.dark-mode .qw-comment-bubble:hover { background: rgba(255,255,255,0.02) !important; }
  `}</style>;
}

function QuestionWiseEmpty({ onReset }: { onReset: () => void }) {
  return (
    <div className="card" style={{ padding: '80px 40px', textAlign: 'center', borderRadius: 'var(--radius)', background: 'var(--surface)', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      <SmallIcon name="refresh" />
      <h3 style={{ fontSize: 24, fontWeight: 800, color: 'var(--text)', marginBottom: 12, fontFamily: 'Outfit' }}>No Feedbacks Under Selected Filters</h3>
      <p style={{ fontSize: 14.5, color: 'var(--text-muted)', maxWidth: 480, margin: '0 auto 30px auto', lineHeight: 1.6 }}>We could not find any response ratings for this dynamic dashboard query. Try resetting your location filters or widening the date selection range.</p>
      <button className="btn btn-primary" onClick={onReset}><SmallIcon name="refresh" />Reset Filters & Refresh</button>
    </div>
  );
}

function QuestionWiseOverview({ npsData }: { npsData: Row }) {
  const total = Number(npsData.total || 0);
  const promoters = Number(npsData.promoters || 0);
  const passives = Number(npsData.passives || 0);
  const detractors = Number(npsData.detractors || 0);
  const npsScore = Number(npsData.npsScore || 0);
  const csatScore = Number.parseFloat(cell(npsData.csatScore || 0)) || 0;
  const promoterPct = total > 0 ? Math.round((promoters / total) * 100) : 0;
  const passivePct = total > 0 ? Math.round((passives / total) * 100) : 0;
  const detractorPct = total > 0 ? Math.round((detractors / total) * 100) : 0;
  const status = npsStatus(npsScore);
  return (
    <div className="card qw-detail-card" style={{ padding: 32, borderRadius: 'var(--radius)', marginBottom: 24, background: 'var(--surface)' }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 32, alignItems: 'center' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 24 }}>
          <div style={{ position: 'relative', width: 100, height: 100, borderRadius: '50%', background: status.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: status.glow, flexShrink: 0 }}>
            <span style={{ fontSize: 32, fontWeight: 900, fontFamily: 'Outfit', color: status.color }}>{npsScore > 0 ? '+' : ''}{npsScore}</span>
          </div>
          <div><h4 style={{ fontSize: 19, fontWeight: 800, color: 'var(--text)', fontFamily: 'Outfit' }}>Overall NPS Score</h4><p style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-muted)', marginTop: 6 }}>Classification: <span className="badge" style={{ background: status.badgeBg, color: status.badgeFg, padding: '4px 10px', fontSize: 10, borderRadius: 8 }}>{status.label}</span></p></div>
        </div>
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}><span style={{ fontSize: 12.5, fontWeight: 800, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.03em' }}>NPS Breakdown</span><span style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-light)' }}>{total} Feedbacks</span></div>
          <div style={{ height: 14, background: 'var(--border)', borderRadius: 7, overflow: 'hidden', display: 'flex', boxShadow: 'inset 0 1px 3px rgba(0,0,0,0.1)' }}><div style={{ width: `${promoterPct}%`, height: '100%', background: 'linear-gradient(90deg, #10b981, #34d399)' }} /><div style={{ width: `${passivePct}%`, height: '100%', background: 'linear-gradient(90deg, #f59e0b, #fbbf24)' }} /><div style={{ width: `${detractorPct}%`, height: '100%', background: 'linear-gradient(90deg, #ef4444, #f87171)' }} /></div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 12, fontSize: 12, fontWeight: 700, gap: 12, flexWrap: 'wrap' }}>
            <span style={{ color: '#10b981', display: 'inline-flex', alignItems: 'center', gap: 6 }}><SmallIcon name="smile" /> Promoters: {promoterPct}%</span>
            <span style={{ color: '#f59e0b', display: 'inline-flex', alignItems: 'center', gap: 6 }}><SmallIcon name="meh" /> Passives: {passivePct}%</span>
            <span style={{ color: '#ef4444', display: 'inline-flex', alignItems: 'center', gap: 6 }}><SmallIcon name="frown" /> Detractors: {detractorPct}%</span>
          </div>
        </div>
        <div className="csat-col" style={{ display: 'flex', alignItems: 'center', gap: 24, borderLeft: '1px solid var(--border)', paddingLeft: 32 }}>
          <div><div style={{ fontSize: 36, fontWeight: 900, fontFamily: 'Outfit', color: 'var(--text)', display: 'flex', alignItems: 'baseline', gap: 4, lineHeight: 1 }}>{csatScore}<span style={{ fontSize: 15, fontWeight: 600, color: 'var(--text-light)' }}>/ 10</span></div><h4 style={{ fontSize: 13, fontWeight: 800, color: 'var(--text-muted)', textTransform: 'uppercase', marginTop: 6, letterSpacing: '0.03em' }}>Avg Recommendation Rating</h4><div style={{ display: 'flex', gap: 2, marginTop: 8 }}>{Array.from({ length: 10 }).map((_, index) => <span key={index} style={{ fontSize: 16, color: index < Math.round(csatScore) ? 'var(--primary)' : 'var(--border)', textShadow: index < Math.round(csatScore) ? '0 0 6px var(--primary-glow)' : 'none' }}>★</span>)}</div></div>
        </div>
      </div>
    </div>
  );
}

function npsStatus(score: number) {
  if (score >= 70) return { label: 'Excellent', bg: 'rgba(16, 185, 129, 0.12)', color: 'var(--success)', glow: '0 8px 24px rgba(16, 185, 129, 0.15)', badgeBg: 'rgba(16, 185, 129, 0.15)', badgeFg: 'var(--success)' };
  if (score >= 40) return { label: 'Strong', bg: 'rgba(16, 185, 129, 0.08)', color: 'var(--success)', glow: '0 8px 20px rgba(16, 185, 129, 0.1)', badgeBg: 'rgba(16, 185, 129, 0.12)', badgeFg: 'var(--success)' };
  if (score >= 10) return { label: 'Healthy', bg: 'rgba(245, 158, 11, 0.1)', color: 'var(--warning)', glow: '0 8px 20px rgba(245, 158, 11, 0.1)', badgeBg: 'rgba(245, 158, 11, 0.12)', badgeFg: 'var(--warning)' };
  return { label: 'Critical', bg: 'rgba(239, 68, 68, 0.1)', color: 'var(--danger)', glow: '0 8px 20px rgba(239, 68, 68, 0.1)', badgeBg: 'rgba(239, 68, 68, 0.12)', badgeFg: 'var(--danger)' };
}

function QuestionWiseSection({ category, rows, canExport, actions }: { category: string; rows: Row[]; canExport: boolean; actions: AdminActions }) {
  return (
    <div style={{ marginBottom: 48 }} className="qw-section-container" data-category={category}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 24, borderBottom: '2px solid var(--border)', paddingBottom: 12, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}><span style={{ width: 6, height: 24, background: 'var(--primary)', borderRadius: 3 }} /><h3 style={{ fontSize: 20, fontWeight: 800, color: 'var(--text)', fontFamily: 'Outfit' }}>{category} Dynamic Questions</h3></div>
        <div style={{ display: 'flex', gap: 8 }} className="no-print"><button className="btn btn-outline" style={{ padding: '6px 12px', fontSize: 12, borderRadius: 8, fontWeight: 700, height: 32 }} disabled={!canExport} title={!canExport ? 'Question Responses export permission is required' : undefined} onClick={() => void actions.exportQuestionWise()}>Export Excel</button><button className="btn btn-outline" style={{ padding: '6px 12px', fontSize: 12, borderRadius: 8, fontWeight: 700, height: 32 }} disabled={!canExport} title={!canExport ? 'Question Responses export permission is required' : undefined} onClick={actions.printQuestionWise}>Print Section</button></div>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(420px, 1fr))', gap: 28 }}>
        {rows.map((row, index) => <QuestionWiseCard row={row} key={cell(row.id || row.text || index)} />)}
      </div>
    </div>
  );
}

function QuestionWiseCard({ row }: { row: Row }) {
  const type = cell(row.type).toLowerCase();
  if (type === 'nps') return <QuestionWiseNpsCard row={row} />;
  if (type === 'star' || type === 'rating') return <QuestionWiseStarCard row={row} />;
  if (type === 'yes_no' || type === 'toggle' || type === 'yes-no') return <QuestionWiseToggleCard row={row} />;
  return <QuestionWiseTextCard row={row} />;
}

function QuestionCardShell({ row, tone, children }: { row: Row; tone: 'primary' | 'accent' | 'muted'; children: React.ReactNode }) {
  const toneStyle = tone === 'primary' ? { background: 'var(--primary-light)', color: 'var(--primary)' } : tone === 'accent' ? { background: 'var(--accent-light)', color: 'var(--accent)' } : { background: '#f1f5f9', color: '#475569' };
  return <div className="card qw-detail-card" style={{ marginBottom: 0, padding: 28, borderRadius: 'var(--radius)', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', minHeight: 380, background: 'var(--surface)' }}><div><div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}><span style={{ fontSize: 10, fontWeight: 800, textTransform: 'uppercase', padding: '4px 10px', borderRadius: 8, letterSpacing: '0.05em', ...toneStyle }}>{cell(row.journey).toUpperCase()} JOURNEY</span><span style={{ fontSize: 12, color: 'var(--text-muted)', fontWeight: 700, display: 'inline-flex', alignItems: 'center', gap: 4 }}>{tone === 'muted' ? '💬' : <SmallIcon name="bar" />} {Number(row.total || 0)} {tone === 'muted' ? 'comments' : 'responses'}</span></div><h4 style={{ fontSize: 16, fontWeight: 800, color: 'var(--text)', lineHeight: 1.5, marginBottom: 20, fontFamily: 'Outfit' }}>{cell(row.text)}</h4></div>{children}</div>;
}

function QuestionWiseNpsCard({ row }: { row: Row }) {
  const total = Number(row.total || row.responses || 0);
  const promoters = Number(row.promoters || 0);
  const passives = Number(row.passives || 0);
  const detractors = Number(row.detractors || 0);
  const promoterPct = total > 0 ? Math.round((promoters / total) * 100) : 0;
  const passivePct = total > 0 ? Math.round((passives / total) * 100) : 0;
  const detractorPct = total > 0 ? Math.round((detractors / total) * 100) : 0;
  const npsScore = Number(row.npsScore || 0);
  const status = npsStatus(npsScore);
  return <QuestionCardShell row={row} tone="primary"><div><div style={{ display: 'grid', gridTemplateColumns: 'minmax(110px, 0.8fr) minmax(0, 1.2fr)', gap: 18, alignItems: 'center', marginBottom: 22, background: 'rgba(0,0,0,0.02)', padding: 16, borderRadius: 14, border: '1px solid var(--border)' }}><div style={{ textAlign: 'center' }}><div style={{ fontSize: 38, fontWeight: 900, fontFamily: 'Outfit', color: status.color, lineHeight: 1 }}>{npsScore > 0 ? '+' : ''}{npsScore}</div><div className="badge" style={{ display: 'inline-flex', marginTop: 8, background: status.badgeBg, color: status.badgeFg, padding: '4px 10px', fontSize: 10, borderRadius: 8, fontWeight: 800 }}>{status.label}</div></div><div><div style={{ fontSize: 12, fontWeight: 800, color: 'var(--text-muted)', textTransform: 'uppercase', marginBottom: 10, letterSpacing: '0.03em' }}>Opening NPS Breakdown</div><div style={{ height: 12, background: 'var(--border)', borderRadius: 6, overflow: 'hidden', display: 'flex', boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.05)' }}><div style={{ width: `${promoterPct}%`, height: '100%', background: 'linear-gradient(90deg, #10b981, #34d399)' }} /><div style={{ width: `${passivePct}%`, height: '100%', background: 'linear-gradient(90deg, #f59e0b, #fbbf24)' }} /><div style={{ width: `${detractorPct}%`, height: '100%', background: 'linear-gradient(90deg, #ef4444, #f87171)' }} /></div></div></div><div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 12 }}><NpsBucket label="Promoters" value={promoters} pct={promoterPct} color="#10b981" /><NpsBucket label="Passives" value={passives} pct={passivePct} color="#f59e0b" /><NpsBucket label="Detractors" value={detractors} pct={detractorPct} color="#ef4444" /></div></div></QuestionCardShell>;
}

function NpsBucket({ label, value, pct, color }: { label: string; value: number; pct: number; color: string }) {
  return <div style={{ border: '1px solid var(--border)', borderRadius: 14, padding: 14, textAlign: 'center', background: 'var(--bg)' }}><div style={{ fontSize: 24, fontWeight: 900, color, fontFamily: 'Outfit' }}>{value}</div><div style={{ fontSize: 10.5, fontWeight: 800, color, textTransform: 'uppercase', letterSpacing: '0.04em', marginTop: 4 }}>{label}</div><div style={{ fontSize: 11.5, color: 'var(--text-muted)', fontWeight: 700, marginTop: 4 }}>{pct}%</div></div>;
}

function QuestionWiseStarCard({ row }: { row: Row }) {
  const total = Number(row.total || 0);
  const average = Number.parseFloat(cell(row.average || 0)) || 0;
  const breakdown = (row.breakdown && typeof row.breakdown === 'object' ? row.breakdown : {}) as Row;
  return <QuestionCardShell row={row} tone="primary"><div><div style={{ display: 'flex', alignItems: 'center', gap: 20, marginBottom: 24, background: 'rgba(0,0,0,0.02)', padding: 16, borderRadius: 14, border: '1px solid var(--border)' }}><div style={{ fontSize: 40, fontWeight: 900, fontFamily: 'Outfit', color: 'var(--text)', lineHeight: 1 }}>{cell(row.average)}</div><div><div style={{ display: 'flex', gap: 3 }}>{[1, 2, 3, 4, 5].map(star => <span key={star} style={{ fontSize: 18, color: star <= Math.round(average) ? '#fbbf24' : 'var(--border)', textShadow: star <= Math.round(average) ? '0 0 8px rgba(251, 191, 36, 0.4)' : 'none' }}>★</span>)}</div><div style={{ fontSize: 12, color: 'var(--text-muted)', fontWeight: 700, marginTop: 4 }}>Average rating score</div></div></div><div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>{[5, 4, 3, 2, 1].map(star => { const count = Number(breakdown[String(star)] || 0); const percentage = total > 0 ? Math.round((count / total) * 100) : 0; return <div key={star} style={{ display: 'flex', alignItems: 'center', gap: 12, fontSize: 12.5 }}><span style={{ width: 24, fontWeight: 800, color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: 2 }}>{star}★</span><div style={{ flex: 1, height: 8, background: 'var(--border)', borderRadius: 4, overflow: 'hidden' }}><div className="qw-progress-fill" style={{ width: `${percentage}%`, height: '100%', background: 'linear-gradient(90deg, #f59e0b, #fbbf24)', borderRadius: 4 }} /></div><span style={{ width: 75, textAlign: 'right', fontWeight: 800, color: 'var(--text-muted)', fontFeatureSettings: "'tnum'" }}>{percentage}% <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-light)' }}>({count})</span></span></div>; })}</div></div></QuestionCardShell>;
}

function QuestionWiseToggleCard({ row }: { row: Row }) {
  const total = Number(row.total || 0);
  const yes = Number(row.yesCount || 0);
  const no = Number(row.noCount || 0);
  const yesPct = total > 0 ? Math.round((yes / total) * 100) : 0;
  const noPct = total > 0 ? Math.round((no / total) * 100) : 0;
  return <QuestionCardShell row={row} tone="accent"><div><div style={{ display: 'flex', gap: 16, marginBottom: 24 }}><div style={{ flex: 1, background: 'rgba(16, 185, 129, 0.06)', padding: 16, borderRadius: 16, textAlign: 'center', border: '1px solid rgba(16, 185, 129, 0.2)', boxShadow: '0 4px 12px rgba(16, 185, 129, 0.03)' }}><div style={{ fontSize: 32, fontWeight: 900, color: '#10b981', fontFamily: 'Outfit' }}>{yes}</div><div style={{ fontSize: 12, fontWeight: 800, color: '#059669', letterSpacing: '0.05em', textTransform: 'uppercase', marginTop: 4 }}>YES ({yesPct}%)</div></div><div style={{ flex: 1, background: 'rgba(239, 68, 68, 0.06)', padding: 16, borderRadius: 16, textAlign: 'center', border: '1px solid rgba(239, 68, 68, 0.2)', boxShadow: '0 4px 12px rgba(239, 68, 68, 0.03)' }}><div style={{ fontSize: 32, fontWeight: 900, color: '#ef4444', fontFamily: 'Outfit' }}>{no}</div><div style={{ fontSize: 12, fontWeight: 800, color: '#dc2626', letterSpacing: '0.05em', textTransform: 'uppercase', marginTop: 4 }}>NO ({noPct}%)</div></div></div><div style={{ height: 12, background: 'var(--border)', borderRadius: 6, overflow: 'hidden', display: 'flex', boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.05)' }}><div style={{ width: `${yesPct}%`, height: '100%', background: 'linear-gradient(90deg, #10b981, #34d399)' }} /><div style={{ width: `${noPct}%`, height: '100%', background: 'linear-gradient(90deg, #ef4444, #f87171)' }} /></div></div></QuestionCardShell>;
}

function QuestionWiseTextCard({ row }: { row: Row }) {
  const responses = Array.isArray(row.textResponses) ? row.textResponses.map(item => item as Row) : [];
  return <QuestionCardShell row={row} tone="muted"><div style={{ display: 'flex', flexDirection: 'column', gap: 14, maxHeight: 230, overflowY: 'auto', paddingRight: 6 }} className="qw-comments-scroll">{responses.length ? responses.map((response, index) => <CommentBubble key={index} response={response} />) : <div style={{ textAlign: 'center', padding: 32, color: 'var(--text-light)' }}><span style={{ fontSize: 24, display: 'block', marginBottom: 8 }}>💬</span><span style={{ fontSize: 12.5, fontWeight: 600 }}>No comments recorded yet.</span></div>}</div></QuestionCardShell>;
}

function CommentBubble({ response }: { response: Row }) {
  const patient = cell(response.patient || 'Anonymous');
  const initial = patient.charAt(0).toUpperCase();
  const colors = [{ bg: 'rgba(233, 30, 99, 0.1)', fg: 'var(--primary)' }, { bg: 'rgba(13, 148, 136, 0.1)', fg: 'var(--accent)' }, { bg: 'rgba(59, 130, 246, 0.1)', fg: '#3b82f6' }, { bg: 'rgba(139, 92, 246, 0.1)', fg: '#8b5cf6' }];
  const color = colors[(initial.charCodeAt(0) || 0) % colors.length];
  const date = response.date ? new Date(String(response.date)) : null;
  return <div style={{ background: 'var(--bg)', padding: '14px 16px', borderRadius: 14, border: '1px solid var(--border)', borderLeft: '4px solid var(--primary)', transition: 'all 0.2s' }} className="qw-comment-bubble"><p style={{ fontSize: 13, color: 'var(--text)', lineHeight: 1.5, fontStyle: 'italic', fontWeight: 550, margin: '0 0 10px 0' }}>"{cell(response.text)}"</p><div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}><div style={{ display: 'flex', alignItems: 'center', gap: 8 }}><div style={{ width: 20, height: 20, borderRadius: '50%', background: color.bg, color: color.fg, fontSize: 10, fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{initial}</div><span style={{ fontSize: 11.5, color: 'var(--text-muted)', fontWeight: 700 }}>{patient}</span></div><span style={{ fontSize: 11, color: 'var(--text-light)', fontWeight: 600 }}>{date && !Number.isNaN(date.getTime()) ? date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) : '-'}</span></div></div>;
}

export function MonthlySummaryTab({
  active,
  rows,
  centers,
  month,
  center,
  loaded,
  canExport,
  actions,
}: {
  active: boolean;
  rows: RegistrationSummary[];
  centers: string[];
  month: string;
  center: string;
  loaded: boolean;
  canExport: boolean;
  actions: AdminActions;
}) {
  const selectedPeriod = month === 'YTD' || month === 'FY_YTD' ? month : 'MONTH';
  const summaryTableRef = useRef<HTMLTableElement | null>(null);
  const summaryPdfRef = useRef<HTMLDivElement | null>(null);
  type SummaryTotals = Record<'newCase' | 'opu' | 'et', { patient: number; feedback: number; npsSum: number; npsCount: number }>;
  const totals = rows.reduce<SummaryTotals>((acc, row) => {
    const newCase = summaryMetricNumbers(row.newCase);
    const opu = summaryMetricNumbers(row.opu);
    const et = summaryMetricNumbers(row.et);
    acc.newCase.patient += newCase.patient;
    acc.newCase.feedback += newCase.feedback;
    acc.newCase.npsSum += newCase.nps;
    acc.newCase.npsCount += 1;
    acc.opu.patient += opu.patient;
    acc.opu.feedback += opu.feedback;
    acc.opu.npsSum += opu.nps;
    acc.opu.npsCount += 1;
    acc.et.patient += et.patient;
    acc.et.feedback += et.feedback;
    acc.et.npsSum += et.nps;
    acc.et.npsCount += 1;
    return acc;
  }, {
    newCase: { patient: 0, feedback: 0, npsSum: 0, npsCount: 0 },
    opu: { patient: 0, feedback: 0, npsSum: 0, npsCount: 0 },
    et: { patient: 0, feedback: 0, npsSum: 0, npsCount: 0 },
  });
  const totalCells = [
    totals.newCase.patient,
    totals.newCase.feedback,
    `${totals.newCase.patient > 0 ? Math.round((totals.newCase.feedback / totals.newCase.patient) * 100) : 0}%`,
    formatAverageNps(totals.newCase.npsSum, totals.newCase.npsCount),
    totals.opu.patient,
    totals.opu.feedback,
    `${totals.opu.patient > 0 ? Math.round((totals.opu.feedback / totals.opu.patient) * 100) : 0}%`,
    formatAverageNps(totals.opu.npsSum, totals.opu.npsCount),
    totals.et.patient,
    totals.et.feedback,
    `${totals.et.patient > 0 ? Math.round((totals.et.feedback / totals.et.patient) * 100) : 0}%`,
    formatAverageNps(totals.et.npsSum, totals.et.npsCount),
  ];
  return (
    <section id="tab-summary" className="tab-content" style={{ display: active ? 'block' : 'none' }}>
      <div className="card">
        <div className="filter-row monthly-summary-filter-row">
          <div className="input-group"><label>Reporting Period</label><select value={selectedPeriod} onChange={(event) => actions.setSummaryMonth(event.target.value === 'MONTH' ? currentMonthValue() : event.target.value)}><option value="MONTH">Selected Month</option><option value="YTD">YTD (Year to Date)</option><option value="FY_YTD">FY-YTD (Financial Year to Date)</option></select></div>
          {selectedPeriod === 'MONTH' ? <div className="input-group"><label>Select Month</label><input type="month" id="summary-month" value={month} onChange={event => actions.setSummaryMonth(event.target.value)} /></div> : null}
          <SelectGroup label="Center" id="summary-center" value={center} options={['ALL', ...centers]} allLabel="All Centers" onChange={actions.setSummaryCenter} />
          <button className="btn btn-primary report-action-btn" onClick={actions.refreshSummary}><SmallIcon name="refresh" />Refresh Summary</button>
          <button className="btn btn-outline report-action-btn" disabled={!canExport} title={!canExport ? 'Monthly Summary export permission is required' : undefined} onClick={() => void actions.exportSummary(summaryTableRef.current)}><SmallIcon name="download" />Export Summary</button>
          <button className={adminPdfActionButtonClassName} disabled={!canExport} title={!canExport ? 'Monthly Summary export permission is required' : undefined} onClick={() => void actions.exportSummaryPdf(summaryPdfRef.current)} style={adminPdfActionButtonStyle}><AdminPdfActionIcon kind="download" />Export PDF</button>
        </div>
        <div id="summary-pdf-content" ref={summaryPdfRef} style={{ marginTop: 32, overflowX: 'auto' }}>
          <h3 style={{ marginBottom: 20 }}>{selectedPeriod === 'MONTH' ? 'Monthly' : selectedPeriod === 'YTD' ? 'Year-to-Date' : 'Financial-Year-to-Date'} Performance Summary</h3>
          <table ref={summaryTableRef} id="summary-table" className="summary-table" style={{ minWidth: 1200, textAlign: 'center' }}>
            <thead><tr style={{ background: '#f8fafc' }}><th rowSpan={2} style={{ background: '#e2e8f0', color: '#0f172a', border: '1px solid var(--border)', textAlign: 'center', verticalAlign: 'middle' }}>Centre Name</th><th colSpan={4} style={{ background: '#7dd3fc', color: '#0369a1', border: '1px solid var(--border)', textAlign: 'center', verticalAlign: 'middle' }}>New Case</th><th colSpan={4} style={{ background: '#fbcfe8', color: '#9d174d', border: '1px solid var(--border)', textAlign: 'center', verticalAlign: 'middle' }}>OPU</th><th colSpan={4} style={{ background: '#fed7aa', color: '#9a3412', border: '1px solid var(--border)', textAlign: 'center', verticalAlign: 'middle' }}>ET</th></tr><tr style={{ background: '#f1f5f9' }}>{['Patient Count', 'Feedbacks Received', '% of Collection', 'NPS Score', 'Patient Count', 'Feedbacks Received', '% of Collection', 'NPS Score', 'Patient Count', 'Feedbacks Received', '% of Collection', 'NPS Score'].map((label, index) => <th key={`summary-header-${index}-${label}`} style={{ fontSize: 10, border: '1px solid var(--border)', textAlign: 'center', verticalAlign: 'middle' }}>{label}</th>)}</tr></thead>
            <tbody id="summary-body">{rows.length ? rows.map((row, index) => <tr key={`${cell(row.center)}-${index}`}><td style={{ textAlign: 'left', fontWeight: 700, background: '#f8fafc', border: '1px solid var(--border)', whiteSpace: 'nowrap', padding: '12px 16px' }}>{cell(row.center)}</td>{[...summaryMetricCells(row.newCase), ...summaryMetricCells(row.opu), ...summaryMetricCells(row.et)].map((value, valueIndex) => <SummaryCell key={valueIndex} value={value} isPercent={valueIndex % 4 === 2} isNps={valueIndex % 4 === 3} />)}</tr>) : <tr><td colSpan={13} style={{ padding: 40, color: 'var(--text-muted)' }}>{loaded ? 'No data found for the selected month.' : 'Please select a month and click "Refresh Summary" to view performance data.'}</td></tr>}</tbody>
            <tfoot id="summary-footer">{rows.length ? <tr style={{ background: 'linear-gradient(135deg, var(--primary), var(--primary-dark))', color: 'white', fontWeight: 800, fontSize: 14, boxShadow: '0 -4px 10px rgba(0,0,0,0.1)' }}><td style={{ textAlign: 'left', padding: 20, borderTop: '2px solid white' }}>Grand Total</td>{totalCells.map((value, index) => <td key={index} style={{ borderTop: '2px solid white' }}>{value}</td>)}</tr> : null}</tfoot>
          </table>
        </div>
      </div>
    </section>
  );
}

function currentMonthValue() {
  const date = new Date();
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
}

function SummaryCell({ value, isPercent, isNps }: { value: string | number; isPercent: boolean; isNps: boolean }) {
  const percentage = isPercent ? Number(String(value).replace('%', '')) : 0;
  const background = isPercent ? (percentage >= 80 ? '#f0fdf4' : percentage > 0 ? '#fff7ed' : 'transparent') : undefined;
  return <td style={{ border: '1px solid var(--border)', background, fontWeight: isNps ? 600 : undefined }}>{value}</td>;
}

function formatAverageNps(sum: number, count: number) {
  if (!count) return '0';
  const value = sum / count;
  return Number.isFinite(value) ? value.toFixed(1) : '0';
}

export function StaffManagementTab({ active, rows, centers, actions, canCreate = true, canUpdate = true, canDelete = true }: { active: boolean; rows: TeamMember[]; centers: string[]; actions: AdminActions; canCreate?: boolean; canUpdate?: boolean; canDelete?: boolean }) {
  const [editing, setEditing] = useState<TeamMember | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [filterCenter, setFilterCenter] = useState('ALL');
  const [name, setName] = useState('');
  const [center, setCenter] = useState(centers[0] || '');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [designation, setDesignation] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [deactivationReason, setDeactivationReason] = useState('');
  const [deletingStaff, setDeletingStaff] = useState<TeamMember | null>(null);
  const [deleteError, setDeleteError] = useState('');
  const assignableCenters = useMemo(() => uniqueOptions(centers), [centers]);
  useEffect(() => { if (!center && assignableCenters[0]) setCenter(assignableCenters[0]); }, [assignableCenters, center]);
  const filtered = rows.filter(row => (!search || cell(row.name).toLowerCase().includes(search.toLowerCase())) && (filterCenter === 'ALL' || cell(row.center_name ?? row.center) === filterCenter));
  const centerOptions = [{ label: 'All Centers', value: 'ALL' }, ...assignableCenters.map(item => ({ label: item, value: item }))];
  const assignableCenterOptions = assignableCenters.map(item => ({ label: item, value: item }));
  const resetForm = () => { setEditing(null); setName(''); setCenter(assignableCenters[0] || ''); setPhoneNumber(''); setDesignation(''); setIsActive(true); setDeactivationReason(''); setFormOpen(false); };
  const openCreateForm = () => { if (!canCreate) return; setEditing(null); setName(''); setCenter(assignableCenters[0] || ''); setPhoneNumber(''); setDesignation(''); setIsActive(true); setDeactivationReason(''); setFormOpen(true); };
  const openEditForm = (staff: TeamMember) => {
    setEditing(staff);
    setName(cell(staff.name));
    setCenter(cell(staff.center_name ?? staff.center) === 'Super Admin' ? MANAGEMENT_CENTER : cell(staff.center_name ?? staff.center ?? assignableCenters[0]));
    setPhoneNumber(typeof staff.phone_number === 'string' ? staff.phone_number : '');
    setDesignation(typeof staff.designation === 'string' ? staff.designation : '');
    setIsActive(staff.is_active !== false);
    setDeactivationReason(typeof staff.deactivation_reason === 'string' ? staff.deactivation_reason : '');
    setFormOpen(true);
  };
  const submit = async () => {
    if (!name.trim()) {
      actions.notify('Name required');
      return;
    }
    if (!isActive && !deactivationReason.trim()) {
      actions.notify('Please enter a reason before marking this staff member inactive.');
      return;
    }
    const payload = { name: name.trim(), center_name: center || centers[0] || '', phone_number: phoneNumber || undefined, designation: designation.trim() || undefined, is_active: isActive, deactivation_reason: !isActive ? deactivationReason.trim() : undefined };
    if (editing?.id) await actions.updateStaff(editing.id, payload);
    else await actions.createStaff(payload);
    resetForm();
  };
  const confirmDeleteStaff = async () => {
    if (!deletingStaff?.id) return;
    try {
      setDeleteError('');
      await actions.deleteStaff(deletingStaff.id);
      setDeletingStaff(null);
    } catch (error) {
      setDeleteError(error instanceof Error ? error.message : 'Unable to delete this staff member.');
    }
  };
  return (
    <section id="tab-staff" className="tab-content" style={{ display: active ? 'block' : 'none' }}>
      <div className="card">
        {tabHeader(
          'Staff Management',
          <button className="btn btn-primary" disabled={!canCreate} title={canCreate ? undefined : 'You do not have permission to add staff.'} style={{ padding: '12px 24px', fontSize: 13, fontWeight: 700, borderRadius: 12, height: 'auto' }} onClick={openCreateForm}>
            + Add Staff Member
          </button>,
        )}
        <div className="filter-row" style={{ display: 'flex', gap: 16, marginBottom: 32, flexWrap: 'wrap', width: '100%' }}>
          <SearchBox id="staff-search-input" value={search} onChange={setSearch} placeholder="Search by staff name..." />
          <div style={{ width: 280, minWidth: 200 }}>
            <ChoicesSelect id="staff-center-filter" value={filterCenter} options={centerOptions} onChange={setFilterCenter} />
          </div>
        </div>
        {formOpen && (
          <UserModalPortal>
            <StaffFormModal
              editing={editing}
              name={name}
              center={center}
              phoneNumber={phoneNumber}
              designation={designation}
              isActive={isActive}
              deactivationReason={deactivationReason}
              assignableCenterOptions={assignableCenterOptions}
              onName={setName}
              onCenter={setCenter}
              onPhoneNumber={setPhoneNumber}
              onDesignation={setDesignation}
              onActiveChange={setIsActive}
              onReasonChange={setDeactivationReason}
              onCancel={resetForm}
              onSave={() => void submit()}
            />
          </UserModalPortal>
        )}
        <StaffTable rows={filtered as TeamMember[]} empty="No staff found." onEdit={openEditForm} onDelete={row => setDeletingStaff(row)} canUpdate={canUpdate} canDelete={canDelete} />
        {deletingStaff && (
          <UserModalPortal>
            <DeleteStaffModal
              staff={deletingStaff}
              error={deleteError}
              onCancel={() => {
                setDeleteError('');
                setDeletingStaff(null);
              }}
              onConfirm={() => void confirmDeleteStaff()}
            />
          </UserModalPortal>
        )}
      </div>
    </section>
  );
}

function StaffFormModal({
  editing,
  name,
  center,
  phoneNumber,
  designation,
  isActive,
  deactivationReason,
  assignableCenterOptions,
  onName,
  onCenter,
  onPhoneNumber,
  onDesignation,
  onActiveChange,
  onReasonChange,
  onCancel,
  onSave,
}: {
  editing: TeamMember | null;
  name: string;
  center: string;
  phoneNumber: string;
  designation: string;
  isActive: boolean;
  deactivationReason: string;
  assignableCenterOptions: Array<{ label: string; value: string }>;
  onName: (value: string) => void;
  onCenter: (value: string) => void;
  onPhoneNumber: (value: string) => void;
  onDesignation: (value: string) => void;
  onActiveChange: (value: boolean) => void;
  onReasonChange: (value: string) => void;
  onCancel: () => void;
  onSave: () => void;
}) {
  return (
    <div className="modal-overlay user-account-modal-overlay" style={{ display: 'flex' }} role="presentation">
      <div className="modal-content admin-premium-modal user-account-modal staff-account-modal" role="dialog" aria-modal="true" aria-labelledby="staff-form-title">
        <button onClick={onCancel} aria-label="Close staff form" className="admin-modal-close">&times;</button>
        <div className="user-account-modal-header">
          <span className="user-account-modal-icon"><SmallIcon name="user" /></span>
          <div>
            <h3 id="staff-form-title">{editing ? 'Edit Staff Member' : 'Add New Staff Member'}</h3>
            <p>{editing ? 'Update staff identity, assigned center, and active status.' : 'Create a staff member for survey attribution and center workflows.'}</p>
          </div>
        </div>
        <div className="user-account-modal-grid staff-account-modal-grid">
          <div className="input-group">
            <UserFormLabel>Staff Name</UserFormLabel>
            <input id="new-staff-name" value={name} onChange={event => onName(event.target.value)} placeholder="Full Name" style={userInputStyle} />
          </div>
          <div className="input-group">
            <UserFormLabel>Assigned Center</UserFormLabel>
            <ChoicesSelect id="new-staff-center" value={center} options={assignableCenterOptions} onChange={onCenter} />
          </div>
          <PhoneNumberField id="new-staff-phone" label="Phone Number" value={phoneNumber} onChange={onPhoneNumber} />
          <div className="input-group">
            <UserFormLabel>Designation</UserFormLabel>
            <input id="new-staff-designation" value={designation} onChange={event => onDesignation(event.target.value)} placeholder="Designation" style={userInputStyle} />
          </div>
          <UserStatusEditor isActive={isActive} reason={deactivationReason} onActiveChange={onActiveChange} onReasonChange={onReasonChange} />
          <div className="user-account-modal-actions">
            <FormActions onCancel={onCancel} onSave={onSave} saveLabel="Save Staff" />
          </div>
        </div>
      </div>
    </div>
  );
}

function StaffTable({ rows, empty, onEdit, onDelete, canUpdate, canDelete }: { rows: TeamMember[]; empty: string; onEdit: (row: TeamMember) => void; onDelete: (row: TeamMember) => void; canUpdate: boolean; canDelete: boolean }) {
  return <div style={{ overflowX: 'auto' }}><table id="staff-table" className="report-table" style={{ marginTop: 0, width: '100%' }}><thead><tr><th style={{ width: '25%', fontFamily: 'Outfit', fontWeight: 800, fontSize: 11.5, letterSpacing: '0.05em', color: 'var(--text-muted)', textTransform: 'uppercase' }}>Name</th><th style={{ width: '20%', fontFamily: 'Outfit', fontWeight: 800, fontSize: 11.5, letterSpacing: '0.05em', color: 'var(--text-muted)', textTransform: 'uppercase' }}>Designation</th><th style={{ width: '18%', fontFamily: 'Outfit', fontWeight: 800, fontSize: 11.5, letterSpacing: '0.05em', color: 'var(--text-muted)', textTransform: 'uppercase' }}>Phone</th><th style={{ width: '16%', fontFamily: 'Outfit', fontWeight: 800, fontSize: 11.5, letterSpacing: '0.05em', color: 'var(--text-muted)', textTransform: 'uppercase' }}>Center</th><th style={{ width: '9%', fontFamily: 'Outfit', fontWeight: 800, fontSize: 11.5, letterSpacing: '0.05em', color: 'var(--text-muted)', textTransform: 'uppercase' }}>Status</th><th style={{ width: '12%', textAlign: 'center', fontFamily: 'Outfit', fontWeight: 800, fontSize: 11.5, letterSpacing: '0.05em', color: 'var(--text-muted)', textTransform: 'uppercase' }}>Actions</th></tr></thead><tbody id="staff-body">{rows.length ? rows.map((row, index) => <tr key={String(row.id ?? index)}><td style={{ padding: 16, verticalAlign: 'middle' }}><strong style={{ color: 'var(--text)', fontWeight: 700, fontFamily: 'Outfit', fontSize: 14.5, display: 'inline-flex', alignItems: 'center', gap: 8 }}><span style={{ color: 'var(--primary)', display: 'inline-flex' }}><SmallIcon name="person" /></span>{cell(row.name)}</strong></td><td style={{ padding: 16, verticalAlign: 'middle' }}>{cell(row.designation)}</td><td style={{ padding: 16, verticalAlign: 'middle', whiteSpace: 'nowrap' }}>{cell(row.phone_number)}</td><td style={{ padding: 16, verticalAlign: 'middle' }}><span className="badge user-center-badge"><span style={{ display: 'inline-flex' }}><SmallIcon name="location" /></span>{cell(row.center_name ?? row.center)}</span></td><td style={{ padding: 16, verticalAlign: 'middle' }}><UserStatusBadge active={row.is_active !== false} /></td><td style={{ padding: 16, textAlign: 'center', verticalAlign: 'middle' }}><StaffRowActions canUpdate={canUpdate} canDelete={canDelete} onEdit={() => onEdit(row)} onDelete={() => onDelete(row)} /></td></tr>) : <tr><td colSpan={6} style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)' }}>{empty}</td></tr>}</tbody></table></div>;
}

function StaffRowActions({ canUpdate, canDelete, onEdit, onDelete }: { canUpdate: boolean; canDelete: boolean; onEdit: () => void; onDelete: () => void }) {
  return <div style={{ display: 'flex', gap: 8, justifyContent: 'center', alignItems: 'center' }}><button className="btn btn-outline edit-btn" disabled={!canUpdate} title={canUpdate ? undefined : 'You do not have permission to edit staff.'} style={{ borderColor: 'rgba(13, 148, 136, 0.2)', color: 'var(--accent)', fontSize: 12, padding: '6px 14px', display: 'inline-flex', alignItems: 'center', gap: 6, fontWeight: 700 }} onClick={onEdit}><SmallIcon name="edit" />Edit</button><button className="user-action-btn user-action-delete" disabled={!canDelete} title={canDelete ? undefined : 'You do not have permission to delete staff.'} onClick={onDelete}><SmallIcon name="trash" /><span>Delete</span></button></div>;
}

function SearchBox({ id, value, onChange, placeholder }: { id: string; value: string; onChange: (value: string) => void; placeholder: string }) {
  return <div style={{ flex: 1, minWidth: 240, position: 'relative' }}><input id={id} value={value} onChange={event => onChange(event.target.value)} placeholder={placeholder} style={{ width: '100%', height: 44, padding: '12px 16px 12px 42px', borderRadius: 14, border: '1px solid var(--border)', fontSize: 13.5, fontWeight: 600, background: 'var(--bg)', color: 'var(--text)', outline: 'none', transition: 'all 0.2s' }} /><span style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)', display: 'flex', pointerEvents: 'none' }}><SmallIcon name="search" /></span></div>;
}

function FormActions({ onCancel, onSave, saveLabel }: { onCancel: () => void; onSave: () => void; saveLabel: string }) {
  return <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', marginTop: 24, borderTop: '1px solid var(--border)', paddingTop: 20 }}><button className="btn btn-outline" style={{ height: 48, padding: '0 28px', borderRadius: 12, fontSize: 13 }} onClick={onCancel}>Cancel</button><button className="btn btn-primary" style={{ height: 48, padding: '0 28px', borderRadius: 12, fontSize: 13, boxShadow: '0 4px 12px var(--primary-glow)', transform: 'none' }} onClick={onSave}><SmallIcon name="save" />{saveLabel}</button></div>;
}

function UserModalPortal({ children }: { children: ReactNode }) {
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  if (!mounted || typeof document === 'undefined') return null;
  return createPortal(children, document.body);
}

function secureRandomIndex(maximum: number) {
  if (maximum < 1) throw new Error('Random selection requires a positive range.');
  const values = new Uint32Array(1);
  const limit = 0x100000000 - (0x100000000 % maximum);
  do {
    globalThis.crypto.getRandomValues(values);
  } while (values[0] >= limit);
  return values[0] % maximum;
}

function createStrongTemporaryPassword(length = 16) {
  const groups = [
    'ABCDEFGHJKLMNPQRSTUVWXYZ',
    'abcdefghijkmnopqrstuvwxyz',
    '23456789',
    '!@#$%&*+-=?',
  ];
  const allCharacters = groups.join('');
  const characters = groups.map(group => group[secureRandomIndex(group.length)]);
  while (characters.length < length) characters.push(allCharacters[secureRandomIndex(allCharacters.length)]);
  for (let index = characters.length - 1; index > 0; index -= 1) {
    const swapIndex = secureRandomIndex(index + 1);
    [characters[index], characters[swapIndex]] = [characters[swapIndex], characters[index]];
  }
  return characters.join('');
}

function PasswordStrengthMeter({ password, excludedIdentity = [] }: { password: string; excludedIdentity?: Array<string | null | undefined> }) {
  const score = [password.length >= 12, /[A-Z]/.test(password), /[a-z]/.test(password), /\d/.test(password), /[^A-Za-z0-9]/.test(password)].filter(Boolean).length;
  const identityParts = excludedIdentity.flatMap(value => String(value || '').toLowerCase().split(/[^a-z0-9]+/)).filter(value => value.length >= 3);
  const containsIdentity = identityParts.some(value => password.toLowerCase().includes(value));
  const accepted = score === 5 && !containsIdentity;
  const label = !password ? 'Enter a password' : containsIdentity ? 'Remove account details' : accepted ? 'Strong - accepted' : score >= 3 ? 'Almost there' : 'Too weak';
  return <div className="password-strength" data-level={accepted ? 5 : Math.min(score, 4)} aria-live="polite"><div className="password-strength-header"><span>Password strength</span><strong>{label}</strong></div><div className="password-strength-track" aria-hidden="true">{[1, 2, 3, 4, 5].map(level => <i key={level} className={(accepted ? 5 : Math.min(score, 4)) >= level ? 'active' : ''} />)}</div></div>;
}

export function UserAccountsTab({ active, rows, centers, roles, allRoles, targetAccessOptions, currentUserId, canCreate = true, canUpdate = true, canDelete = true, actions }: { active: boolean; rows: AdminUser[]; centers: string[]; roles: AccessRole[]; allRoles: AccessRole[]; targetAccessOptions: { businessDomains: Array<{ key: string; displayName: string }>; accountableTeams: Array<{ key: string; displayName: string }> }; currentUserId?: string; canCreate?: boolean; canUpdate?: boolean; canDelete?: boolean; adminOtpVerified: boolean; actions: AdminActions }) {
  const passwordInputRef = useRef<HTMLInputElement | null>(null);
  const [editing, setEditing] = useState<AdminUser | null>(null);
  const [deletingUser, setDeletingUser] = useState<AdminUser | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [formError, setFormError] = useState('');
  const [deleteError, setDeleteError] = useState('');
  const [bulkLogoutOpen, setBulkLogoutOpen] = useState(false);
  const [selectedLogoutRoles, setSelectedLogoutRoles] = useState<string[]>([]);
  const [logoutUser, setLogoutUser] = useState<AdminUser | null>(null);
  const [logoutError, setLogoutError] = useState('');
  const [unlockUser, setUnlockUser] = useState<AdminUser | null>(null);
  const [unlockError, setUnlockError] = useState('');
  const [welcomeUser, setWelcomeUser] = useState<AdminUser | null>(null);
  const [welcomePassword, setWelcomePassword] = useState('');
  const [welcomePasswordVisible, setWelcomePasswordVisible] = useState(false);
  const [welcomeError, setWelcomeError] = useState('');
  const [welcomeSending, setWelcomeSending] = useState(false);
  const [userSearch, setUserSearch] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [resetPasswordOnFirstLogin, setResetPasswordOnFirstLogin] = useState(false);
  const [firstName, setFirstName] = useState('');
  const [middleName, setMiddleName] = useState('');
  const [lastName, setLastName] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [email, setEmail] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [deactivationReason, setDeactivationReason] = useState('');
  const [center, setCenter] = useState(centers[0] || '');
  const [centerNames, setCenterNames] = useState<string[]>(centers[0] ? [centers[0]] : []);
  const [role, setRole] = useState('');
  const [businessReportDomains, setBusinessReportDomains] = useState<string[]>([]);
  const [businessReportTeams, setBusinessReportTeams] = useState<string[]>([]);
  const [otp, setOtp] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [otpUnlocked, setOtpUnlocked] = useState(false);
  const onlineUsers = rows.filter(user => user.is_online).length;
  const normalizedUserSearch = userSearch.trim().toLowerCase();
  const filteredRows = useMemo(
    () => normalizedUserSearch ? rows.filter(user => userMatchesSearch(user, normalizedUserSearch)) : rows,
    [normalizedUserSearch, rows],
  );
  const logoutRoleOptions = useMemo(
    () => allRoles.filter(item => item.is_active && !isProtectedLogoutRole(item.key)).map(item => ({ key: item.key, name: item.name })),
    [allRoles],
  );
  useEffect(() => { if (!center && centers[0]) setCenter(centers[0]); }, [center, centers]);
  useEffect(() => { if (!password) setResetPasswordOnFirstLogin(false); }, [password]);
  const isSuperadminEdit = Boolean(editing?.is_superadmin);
  const isRootRole = role === 'SUPERADMIN';
  const showCenter = !isRootRole && !isSuperadminEdit;
  const showOtpSection = isSuperadminEdit;
  const showPassword = !isSuperadminEdit || otpUnlocked;
  const selectedRole = allRoles.find((item) => item.key === role);
  const showTargetScope = Boolean(selectedRole?.permissions.some((permission) => permission === 'business_reports.manage' || permission.startsWith('business_reports.targets.')));
  const roleOptions = useMemo(
    () => roles.filter(item => !item.is_root_only || isSuperadminEdit).map(item => ({ label: item.name, value: item.key })),
    [isSuperadminEdit, roles],
  );
  const defaultRoleKey = useMemo(
    () => roleOptions.find(option => option.value === 'REPORTS')?.value || roleOptions[0]?.value || '',
    [roleOptions],
  );
  const centerOptions = centers.map(item => ({ label: item, value: item }));
  const displayNameOptions = displayNameCandidates(firstName, middleName, lastName, username).map(item => ({ label: item, value: item }));
  useEffect(() => {
    if (!displayNameOptions.some(option => option.value === displayName)) {
      setDisplayName(displayNameOptions[0]?.value || username.trim());
    }
  }, [displayName, displayNameOptions, username]);
  useEffect(() => {
    if (!isSuperadminEdit && defaultRoleKey && !roleOptions.some(option => option.value === role)) {
      setRole(defaultRoleKey);
    }
  }, [defaultRoleKey, isSuperadminEdit, role, roleOptions]);
  useEffect(() => {
    if (!showTargetScope) return;
    if (!businessReportDomains.length) setBusinessReportDomains(targetAccessOptions.businessDomains.map((item) => item.key));
    if (!businessReportTeams.length) setBusinessReportTeams(targetAccessOptions.accountableTeams.map((item) => item.key));
  }, [businessReportDomains.length, businessReportTeams.length, showTargetScope, targetAccessOptions]);
  const toggleCenter = (centerName: string) => {
    setCenterNames(previous => {
      const next = previous.includes(centerName) ? previous.filter(item => item !== centerName) : [...previous, centerName];
      setCenter(next[0] || '');
      return next;
    });
  };
  const resetForm = () => {
    setEditing(null);
    setUsername('');
    setPassword('');
    setResetPasswordOnFirstLogin(false);
    setFirstName('');
    setMiddleName('');
    setLastName('');
    setDisplayName('');
    setEmail('');
    setPhoneNumber('');
    setIsActive(true);
    setDeactivationReason('');
    setCenter(centers[0] || '');
    setCenterNames(centers[0] ? [centers[0]] : []);
    setRole(defaultRoleKey);
    setBusinessReportDomains([]);
    setBusinessReportTeams([]);
    setOtp('');
    setOtpSent(false);
    setOtpUnlocked(false);
    setFormError('');
    setFormOpen(false);
  };
  const openCreateForm = () => {
    setEditing(null);
    setUsername('');
    setPassword('');
    setResetPasswordOnFirstLogin(false);
    setFirstName('');
    setMiddleName('');
    setLastName('');
    setDisplayName(username.trim());
    setEmail('');
    setPhoneNumber('');
    setIsActive(true);
    setDeactivationReason('');
    setCenter(centers[0] || '');
    setCenterNames(centers[0] ? [centers[0]] : []);
    setRole(defaultRoleKey);
    setBusinessReportDomains([]);
    setBusinessReportTeams([]);
    setOtp('');
    setOtpSent(false);
    setOtpUnlocked(false);
    setFormError('');
    setFormOpen(true);
  };
  const openEditForm = (user: AdminUser) => {
    const userRole = (user.roles && user.roles[0]) || user.role || (user.is_superadmin ? 'SUPERADMIN' : 'REPORTS');
    setEditing(user);
    setUsername(user.username);
    setPassword('');
    setResetPasswordOnFirstLogin(false);
    setFirstName(user.first_name ?? '');
    setMiddleName(user.middle_name ?? '');
    setLastName(user.last_name ?? '');
    setDisplayName(user.display_name || displayNameFallback(user));
    setEmail(user.email ?? '');
    setPhoneNumber(user.phone_number ?? '');
    setIsActive(user.is_active !== false);
    setDeactivationReason(user.deactivation_reason ?? '');
    const accessCenters = (user.centerAccess || []).filter(item => item && item !== 'ALL');
    const nextCenters = accessCenters.length ? accessCenters : user.center_name && user.center_name !== 'Super Admin' ? [user.center_name] : centers[0] ? [centers[0]] : [];
    setCenter(nextCenters[0] || centers[0] || '');
    setCenterNames(nextCenters);
    setRole(userRole);
    setBusinessReportDomains(user.businessReportDomainAccess?.filter((item) => item !== 'ALL') || []);
    setBusinessReportTeams(user.businessReportTeamAccess?.filter((item) => item !== 'ALL') || []);
    setOtp('');
    setOtpSent(false);
    setOtpUnlocked(false);
    setFormError('');
    setFormOpen(true);
  };
  const sendOtp = async () => {
    try {
      setFormError('');
      setOtpUnlocked(false);
      await actions.sendAdminOtp();
      setOtpSent(true);
    } catch (error) {
      setFormError(error instanceof Error ? error.message : 'Unable to send OTP. Please try again.');
    }
  };
  const verifyOtp = async () => {
    if (!otp) {
      setFormError('Please enter the 6-digit OTP code.');
      return;
    }
    try {
      setFormError('');
      await actions.verifyAdminOtp(otp);
      setOtpUnlocked(true);
      window.setTimeout(() => passwordInputRef.current?.focus(), 0);
    } catch (error) {
      setOtpUnlocked(false);
      setFormError(error instanceof Error ? error.message : 'OTP verification failed. Please try again.');
    }
  };
  const submit = async () => {
    setFormError('');
    if (!username.trim()) {
      setFormError('Username required');
      return;
    }
    if (!editing?.id && !password) {
      setFormError('Password required');
      return;
    }
    if (password && (password.length < 12 || !/[A-Z]/.test(password) || !/[a-z]/.test(password) || !/\d/.test(password) || !/[^A-Za-z0-9]/.test(password))) {
      setFormError('Password must be at least 12 characters and include uppercase, lowercase, number, and special characters.');
      return;
    }
    if (resetPasswordOnFirstLogin && !/^\+\d{6,15}$/.test(phoneNumber)) {
      setFormError('A valid phone number is required to send the first-login WhatsApp OTP.');
      return;
    }
    if (email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      setFormError('Enter a valid email address.');
      return;
    }
    if (!editing && !email.trim()) { setFormError('Email address is required to send the welcome notification.'); return; }
    if (!editing && !/^\+\d{6,15}$/.test(phoneNumber)) { setFormError('A valid phone number is required for first-login WhatsApp verification.'); return; }
    if (showCenter && centerNames.length === 0) {
      setFormError('Select at least one assigned center.');
      return;
    }
    if (!role || !roleOptions.some(option => option.value === role)) {
      setFormError('Role list is not loaded from database. Refresh and try again.');
      return;
    }
    if (showTargetScope && (!businessReportDomains.length || !businessReportTeams.length)) {
      setFormError('Select at least one Business Reports domain and accountable team.');
      return;
    }
    if (!isActive && !deactivationReason.trim()) {
      setFormError('Please enter a reason before marking this user inactive.');
      return;
    }
    if (isSuperadminEdit && password && !otp) {
      setFormError('Please enter the verification OTP sent to the configured IT admin email to change the superadmin password.');
      return;
    }
    const payload = {
      username: username.trim(),
      password: password || undefined,
      display_name: displayName.trim() || displayNameOptions[0]?.value || username.trim(),
      first_name: firstName.trim() || undefined,
      middle_name: middleName.trim() || undefined,
      last_name: lastName.trim() || undefined,
      email: email.trim() || undefined,
      phone_number: phoneNumber || undefined,
      center_name: showCenter ? (centerNames[0] || center) : MANAGEMENT_CENTER,
      center_names: showCenter ? centerNames : ['ALL'],
      business_report_domain_keys: showTargetScope ? businessReportDomains : [],
      business_report_team_keys: showTargetScope ? businessReportTeams : [],
      role,
      is_superadmin: role === 'SUPERADMIN' || isSuperadminEdit,
      admin_otp: otp || undefined,
      password_reset_required: editing ? Boolean(password && resetPasswordOnFirstLogin) : true,
      is_active: isSuperadminEdit ? true : isActive,
      deactivation_reason: !isActive ? deactivationReason.trim() : undefined,
    };
    try {
      if (editing?.id) await actions.updateUser(editing.id, payload);
      else await actions.createUser(payload);
      resetForm();
    } catch (error) {
      setFormError(error instanceof Error ? error.message : 'Unable to save user account. Please check the details and try again.');
    }
  };
  const requestDeleteUser = (user: AdminUser) => {
    setDeleteError('');
    setDeletingUser(user);
  };
  const confirmDeleteUser = async () => {
    if (!deletingUser?.id) return;
    try {
      setDeleteError('');
      await actions.deleteUser(deletingUser.id);
      setDeletingUser(null);
    } catch (error) {
      setDeleteError(error instanceof Error ? error.message : 'Unable to delete this user account.');
    }
  };
  const toggleLogoutRole = (roleKey: string) => {
    setSelectedLogoutRoles(previous => previous.includes(roleKey) ? previous.filter(item => item !== roleKey) : [...previous, roleKey]);
  };
  const confirmBulkLogout = async () => {
    if (!selectedLogoutRoles.length) {
      setLogoutError('Select at least one role.');
      return;
    }
    try {
      setLogoutError('');
      await actions.forceLogoutUsersByRole(selectedLogoutRoles);
      setBulkLogoutOpen(false);
      setSelectedLogoutRoles([]);
    } catch (error) {
      setLogoutError(error instanceof Error ? error.message : 'Unable to logout selected users.');
    }
  };
  const requestLogoutUser = (user: AdminUser) => {
    setLogoutError('');
    setLogoutUser(user);
  };
  const confirmLogoutUser = async () => {
    if (!logoutUser?.id) return;
    try {
      setLogoutError('');
      await actions.forceLogoutUser(logoutUser.id);
      setLogoutUser(null);
    } catch (error) {
      setLogoutError(error instanceof Error ? error.message : 'Unable to logout this user.');
    }
  };
  const requestUnlockUser = (user: AdminUser) => {
    setUnlockError('');
    setUnlockUser(user);
  };
  const confirmUnlockUser = async () => {
    if (!unlockUser?.id) return;
    try {
      setUnlockError('');
      await actions.unlockUserLogin(unlockUser.id);
      setUnlockUser(null);
    } catch (error) {
      setUnlockError(error instanceof Error ? error.message : 'Unable to unlock this user.');
    }
  };
  const confirmResendWelcome = async () => {
    if (!welcomeUser?.id || welcomeSending) return;
    const strongPassword = welcomePassword.length >= 12 && /[A-Z]/.test(welcomePassword) && /[a-z]/.test(welcomePassword) && /\d/.test(welcomePassword) && /[^A-Za-z0-9]/.test(welcomePassword);
    const identityParts = [welcomeUser.username, welcomeUser.email]
      .flatMap(value => String(value || '').toLowerCase().split(/[^a-z0-9]+/))
      .filter(value => value.length >= 3);
    if (!strongPassword) {
      setWelcomeError('Password must contain at least 12 characters, including uppercase, lowercase, number, and special characters.');
      return;
    }
    if (identityParts.some(value => welcomePassword.toLowerCase().includes(value))) {
      setWelcomeError('Temporary password must not contain the username or email name.');
      return;
    }
    try {
      setWelcomeError('');
      setWelcomeSending(true);
      await actions.resendUserWelcome(welcomeUser.id, welcomePassword);
      setWelcomeUser(null);
      setWelcomePassword('');
      setWelcomePasswordVisible(false);
    } catch (error) {
      setWelcomeError(error instanceof Error ? error.message : 'Unable to resend welcome email.');
    } finally {
      setWelcomeSending(false);
    }
  };
  const headerActions = (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', justifyContent: 'flex-end', flex: '1 1 620px' }}>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }} aria-label="User account totals">
        <span className="badge" style={{ background: 'rgba(233, 30, 99, 0.08)', color: 'var(--primary)', border: '1px solid rgba(233, 30, 99, 0.16)', fontWeight: 850, textTransform: 'none', fontSize: 12 }}>Total Users: {rows.length}</span>
        <span className="badge" style={{ background: 'rgba(16, 185, 129, 0.1)', color: '#047857', border: '1px solid rgba(16, 185, 129, 0.2)', fontWeight: 850, textTransform: 'none', fontSize: 12 }}>Online: {onlineUsers}</span>
      </div>
      <div style={{ position: 'relative', flex: '1 1 260px', maxWidth: 380, minWidth: 220 }}>
        <input
          aria-label="Search users"
          value={userSearch}
          onChange={event => setUserSearch(event.target.value)}
          placeholder="Search username, name, email, phone..."
          style={{ width: '100%', height: 42, borderRadius: 12, border: '1px solid var(--border)', background: 'var(--surface-solid)', color: 'var(--text)', padding: '0 38px 0 38px', fontSize: 13, fontWeight: 700, outline: 'none' }}
        />
        <span style={{ position: 'absolute', left: 13, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)', display: 'inline-flex', pointerEvents: 'none' }}><SmallIcon name="search" /></span>
        {userSearch && <button type="button" aria-label="Clear user search" onClick={() => setUserSearch('')} style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', border: 0, background: 'transparent', color: 'var(--text-muted)', display: 'inline-flex', cursor: 'pointer', padding: 4 }}><SmallIcon name="close" /></button>}
      </div>
      <button className="btn btn-outline" disabled={!canUpdate || onlineUsers === 0} title={!canUpdate ? 'You do not have permission to update users.' : onlineUsers === 0 ? 'No online users found.' : undefined} style={{ padding: '12px 18px', fontSize: 13, fontWeight: 800, borderRadius: 12, height: 'auto' }} onClick={() => { setLogoutError(''); setBulkLogoutOpen(true); }}>Logout Users</button>
      <button className="btn btn-primary" disabled={!canCreate} title={canCreate ? undefined : 'You do not have permission to create users.'} style={{ padding: '12px 24px', fontSize: 13, fontWeight: 700, borderRadius: 12, height: 'auto' }} onClick={openCreateForm}>+ Create New Account</button>
    </div>
  );
  return (
    <section id="tab-users" className="tab-content" style={{ display: active ? 'block' : 'none' }}>
      <div className="card">
        {tabHeader('User Accounts', headerActions)}
        {formOpen && (
          <UserModalPortal>
            <div className="modal-overlay user-account-modal-overlay" style={{ display: 'flex' }} role="presentation">
              <div className="modal-content admin-premium-modal user-account-modal" role="dialog" aria-modal="true" aria-labelledby="user-form-title">
                <button onClick={resetForm} aria-label="Close user account form" className="admin-modal-close">&times;</button>
                <div className="user-account-modal-header">
                  <span className="user-account-modal-icon"><SmallIcon name="user" /></span>
                  <div>
                    <h3 id="user-form-title">{editing ? 'Edit User Account' : 'Create New Account'}</h3>
                    <p>{editing ? 'Update identity, role, center access, and account status.' : 'Create a secured RBAC user account with assigned centers.'}</p>
                  </div>
                </div>
                {formError && <div className="user-modal-error" role="alert">{formError}</div>}
            <input type="hidden" id="edit-user-id" value={editing?.id ?? ''} readOnly />
            <div className="user-account-modal-grid">
              <div className="input-group"><UserFormLabel>Username</UserFormLabel><input type="text" id="new-user-name" value={username} disabled={isSuperadminEdit} onChange={event => setUsername(event.target.value)} placeholder="Enter username" style={userInputStyle} /></div>
              <div className="input-group"><UserFormLabel>First Name</UserFormLabel><input type="text" id="new-user-first-name" value={firstName} onChange={event => setFirstName(event.target.value)} placeholder="First name" style={userInputStyle} /></div>
              <div className="input-group"><UserFormLabel>Middle Name</UserFormLabel><input type="text" id="new-user-middle-name" value={middleName} onChange={event => setMiddleName(event.target.value)} placeholder="Middle name" style={userInputStyle} /></div>
              <div className="input-group"><UserFormLabel>Last Name</UserFormLabel><input type="text" id="new-user-last-name" value={lastName} onChange={event => setLastName(event.target.value)} placeholder="Last name" style={userInputStyle} /></div>
              <div className="input-group"><UserFormLabel>Display Name</UserFormLabel><ChoicesSelect id="new-user-display-name" value={displayName} options={displayNameOptions} searchEnabled={false} onChange={setDisplayName} /></div>
              <div className="input-group"><UserFormLabel>Email Address{!editing ? ' (required)' : ''}</UserFormLabel><input required={!editing} type="email" id="new-user-email" value={email} onChange={event => setEmail(event.target.value)} placeholder="name@example.com" style={userInputStyle} /></div>
              <PhoneNumberField id="new-user-phone" label={resetPasswordOnFirstLogin ? 'Phone Number (required for first-login OTP)' : 'Phone Number'} value={phoneNumber} onChange={setPhoneNumber} />
              {!isSuperadminEdit && <UserStatusEditor isActive={isActive} reason={deactivationReason} onActiveChange={setIsActive} onReasonChange={setDeactivationReason} />}
              {showPassword && <div className="input-group" id="user-password-group"><UserFormLabel>New Password</UserFormLabel><input ref={passwordInputRef} type="password" id="new-user-pass" value={password} onChange={event => setPassword(event.target.value)} placeholder="Enter password" style={userInputStyle} /><PasswordStrengthMeter password={password} /></div>}
              {showPassword && (
                <label className="user-reset-password-check" style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '12px 14px', border: '1px solid var(--border)', borderRadius: 14, background: 'var(--surface-muted)', cursor: password ? 'pointer' : 'not-allowed' }}>
                  <input type="checkbox" checked={editing ? resetPasswordOnFirstLogin : true} disabled={!editing || !password} onChange={event => setResetPasswordOnFirstLogin(event.target.checked)} style={{ marginTop: 3, accentColor: 'var(--primary)' }} />
                  <span style={{ display: 'grid', gap: 2 }}>
                    <span style={{ fontFamily: 'Outfit, sans-serif', fontSize: 12, fontWeight: 900, color: password ? 'var(--text)' : 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{editing ? 'Reset Password on First Time Login' : 'Password change required at first login'}</span>
                    <span style={{ fontSize: 11.5, fontWeight: 650, color: 'var(--text-muted)', lineHeight: 1.35 }}>User must change this password after signing in.</span>
                  </span>
                </label>
              )}
              <div className="input-group"><UserFormLabel>Role</UserFormLabel><ChoicesSelect id="new-user-role" value={role} options={roleOptions} disabled={isSuperadminEdit} searchEnabled={false} onChange={setRole} /></div>
              {showCenter && <div className="input-group" id="user-center-group"><UserFormLabel>Primary Center</UserFormLabel><ChoicesSelect id="new-user-center" value={center} options={centerOptions} onChange={value => { setCenter(value); setCenterNames([value]); }} /></div>}
              {showCenter && <MultiCenterPicker centers={centers} selected={centerNames} onToggle={toggleCenter} onSelectAll={() => { setCenterNames(centers); setCenter(centers[0] || ''); }} onClearAll={() => { setCenterNames([]); setCenter(''); }} />}
              {showTargetScope && <MultiAccessPicker label="Business Reports Domains" options={targetAccessOptions.businessDomains} selected={businessReportDomains} onChange={setBusinessReportDomains} />}
              {showTargetScope && <MultiAccessPicker label="Accountable Target Teams" options={targetAccessOptions.accountableTeams} selected={businessReportTeams} onChange={setBusinessReportTeams} />}
              {showOtpSection && <div className="input-group user-modal-wide" id="admin-otp-section" style={{ background: otpUnlocked ? '#f0fdf4' : 'var(--surface-solid)', padding: 14, borderRadius: 16, border: `1px solid ${otpUnlocked ? '#22c55e' : 'var(--border)'}`, borderLeft: `4px solid ${otpUnlocked ? '#22c55e' : 'var(--primary)'}` }}><label style={{ fontFamily: 'Outfit', fontWeight: 800, fontSize: 11, textTransform: 'uppercase', color: otpUnlocked ? '#16a34a' : 'var(--primary)', display: 'block', marginBottom: 8, letterSpacing: '0.05em' }}>ADMIN SECURITY VERIFICATION</label><div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}><input type="text" id="admin-user-otp" value={otp} disabled={otpUnlocked} onChange={event => { setOtp(event.target.value); setOtpUnlocked(false); }} placeholder="Enter 6-digit OTP" style={{ ...userInputStyle, flex: 1, minWidth: 150, background: otpUnlocked ? '#e2e8f0' : 'var(--bg)', color: otpUnlocked ? '#64748b' : 'var(--text)' }} /><button type="button" className="btn btn-outline" id="btn-send-otp" disabled={otpUnlocked} onClick={() => void sendOtp()} style={{ height: 42, borderRadius: 12, fontWeight: 700, fontSize: 12, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6, padding: '0 16px', opacity: otpUnlocked ? 0.5 : 1 }}>{otpSent ? 'Resend OTP' : 'Send OTP'}</button>{!otpUnlocked && <button type="button" className="btn btn-primary" id="btn-unlock-otp" onClick={() => void verifyOtp()} style={{ height: 42, borderRadius: 12, fontWeight: 700, fontSize: 12, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6, padding: '0 16px', boxShadow: '0 4px 12px var(--primary-glow)' }}>Verify & Unlock</button>}{otpUnlocked && <button type="button" className="btn btn-primary" id="btn-unlock-otp" disabled style={{ height: 42, borderRadius: 12, fontWeight: 700, fontSize: 12, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6, padding: '0 16px', background: '#22c55e', boxShadow: 'none' }}>Verified</button>}</div></div>}
              <div className="user-account-modal-actions"><FormActions onCancel={resetForm} onSave={() => void submit()} saveLabel="Save User Account" /></div>
            </div>
              </div>
            </div>
          </UserModalPortal>
        )}
        <UserTable rows={filteredRows} empty={normalizedUserSearch ? 'No users match your search.' : 'No users found.'} roles={allRoles} currentUserId={currentUserId} canUpdate={canUpdate} canDelete={canDelete} onEdit={openEditForm} onDelete={requestDeleteUser} onForceLogout={requestLogoutUser} onUnlockLogin={requestUnlockUser} onResendWelcome={user=>{setWelcomeUser(user);setWelcomePassword('');setWelcomePasswordVisible(false);setWelcomeError('');setWelcomeSending(false)}} />
        {welcomeUser && (
          <UserModalPortal>
            <div className="modal-overlay user-account-modal-overlay" style={{ display: 'flex' }} role="presentation">
              <div className="welcome-resend-modal" role="dialog" aria-modal="true" aria-labelledby="resend-welcome-title" aria-describedby="resend-welcome-description" aria-busy={welcomeSending}>
                <button className="admin-modal-close welcome-resend-close" disabled={welcomeSending} onClick={() => setWelcomeUser(null)} aria-label="Close resend welcome email dialog">&times;</button>
                <header className="welcome-resend-header">
                  <span className="welcome-resend-icon"><SmallIcon name="message" /></span>
                  <div>
                    <span className="welcome-resend-eyebrow">Secure account invitation</span>
                    <h3 id="resend-welcome-title">Resend Welcome Email</h3>
                    <p id="resend-welcome-description">Issue a new temporary password and send fresh sign-in instructions.</p>
                  </div>
                </header>
                <div className="welcome-resend-body">
                  <div className="welcome-resend-user">
                    <span className="welcome-resend-user-avatar" aria-hidden="true">{displayNameFallback(welcomeUser).charAt(0).toUpperCase()}</span>
                    <div><strong>{displayNameFallback(welcomeUser)}</strong><span>{welcomeUser.username}{welcomeUser.email ? ` · ${welcomeUser.email}` : ''}</span></div>
                  </div>
                  <div className="welcome-resend-notice"><SmallIcon name="shield" /><span>Existing sessions will be revoked. The user must replace this password at first login.</span></div>
                  {welcomeError && <div className="user-modal-error" role="alert">{welcomeError}</div>}
                  <div className="welcome-password-heading">
                    <label htmlFor="welcome-temporary-password">Temporary password</label>
                    <button
                      type="button"
                      className="welcome-create-password"
                      disabled={welcomeSending}
                      onClick={() => {
                        setWelcomePassword(createStrongTemporaryPassword());
                        setWelcomePasswordVisible(true);
                        setWelcomeError('');
                      }}
                    ><SmallIcon name="refresh" /> Create Password</button>
                  </div>
                  <div className="welcome-password-field">
                    <input id="welcome-temporary-password" type={welcomePasswordVisible ? 'text' : 'password'} autoComplete="new-password" disabled={welcomeSending} value={welcomePassword} onChange={event => { setWelcomePassword(event.target.value); setWelcomeError(''); }} placeholder="Enter or securely create a password" />
                    <button type="button" disabled={welcomeSending || !welcomePassword} onClick={() => setWelcomePasswordVisible(value => !value)} aria-label={welcomePasswordVisible ? 'Hide temporary password' : 'Show temporary password'}>{welcomePasswordVisible ? 'Hide' : 'Show'}</button>
                  </div>
                  <p className="welcome-password-help">Generated passwords are created securely in this browser and are not sent until you confirm.</p>
                  <PasswordStrengthMeter password={welcomePassword} excludedIdentity={[welcomeUser.username, welcomeUser.email]} />
                  <div className="welcome-password-rules" aria-label="Password requirements">
                    {([
                      ['12+ characters', welcomePassword.length >= 12],
                      ['Uppercase letter', /[A-Z]/.test(welcomePassword)],
                      ['Lowercase letter', /[a-z]/.test(welcomePassword)],
                      ['Number', /\d/.test(welcomePassword)],
                      ['Special character', /[^A-Za-z0-9]/.test(welcomePassword)],
                    ] as const).map(([label, met]) => <span key={label} className={met ? 'met' : ''}><b aria-hidden="true">{met ? '✓' : '·'}</b>{label}</span>)}
                  </div>
                </div>
                <footer className="welcome-resend-footer">
                  <button className="btn btn-outline" disabled={welcomeSending} onClick={() => setWelcomeUser(null)}>Cancel</button>
                  <button className="btn btn-primary welcome-resend-submit" disabled={welcomeSending || !welcomePassword} onClick={() => void confirmResendWelcome()}>{welcomeSending && <span className="welcome-action-spinner" aria-hidden="true" />}<span aria-live="polite">{welcomeSending ? 'Sending welcome email...' : 'Save & Resend'}</span></button>
                </footer>
                {welcomeSending && <p className="welcome-resend-progress" role="status">Saving the temporary password and sending the welcome email. Please wait.</p>}
              </div>
            </div>
          </UserModalPortal>
        )}
        {bulkLogoutOpen && (
          <UserModalPortal>
            <BulkLogoutModal
              roles={logoutRoleOptions}
              selected={selectedLogoutRoles}
              error={logoutError}
              onToggle={toggleLogoutRole}
              onCancel={() => {
                setLogoutError('');
                setBulkLogoutOpen(false);
              }}
              onConfirm={() => void confirmBulkLogout()}
            />
          </UserModalPortal>
        )}
        {logoutUser && (
          <UserModalPortal>
            <ForceLogoutUserModal
              user={logoutUser}
              error={logoutError}
              onCancel={() => {
                setLogoutError('');
                setLogoutUser(null);
              }}
              onConfirm={() => void confirmLogoutUser()}
            />
          </UserModalPortal>
        )}
        {unlockUser && (
          <UserModalPortal>
            <UnlockLoginUserModal
              user={unlockUser}
              error={unlockError}
              onCancel={() => {
                setUnlockError('');
                setUnlockUser(null);
              }}
              onConfirm={() => void confirmUnlockUser()}
            />
          </UserModalPortal>
        )}
        {deletingUser && (
          <UserModalPortal>
            <DeleteUserModal
              user={deletingUser}
              error={deleteError}
              onCancel={() => {
                setDeleteError('');
                setDeletingUser(null);
              }}
              onConfirm={() => void confirmDeleteUser()}
            />
          </UserModalPortal>
        )}
      </div>
    </section>
  );
}

const userInputStyle = {
  padding: '12px 16px',
  borderRadius: 12,
  border: '1px solid var(--border)',
  fontSize: 13.5,
  fontWeight: 600,
  background: 'var(--bg)',
  color: 'var(--text)',
  transition: 'all 0.2s',
  outline: 'none',
  height: 48,
} as const;

function MultiCenterPicker({ centers, selected, onToggle, onSelectAll, onClearAll }: { centers: string[]; selected: string[]; onToggle: (center: string) => void; onSelectAll: () => void; onClearAll: () => void }) {
  const allSelected = centers.length > 0 && centers.every(centerName => selected.includes(centerName));
  return (
    <div className="input-group user-modal-wide" id="user-center-access-group">
      <UserFormLabel>Center Access</UserFormLabel>
      <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, marginBottom: 10, fontSize: 12, fontWeight: 800, color: 'var(--text-muted)', cursor: 'pointer' }}>
        <input type="checkbox" checked={allSelected} onChange={event => event.target.checked ? onSelectAll() : onClearAll()} />
        Select All Centers
      </label>
      <div className="user-center-picker-list">
        {centers.map(centerName => (
          <label key={centerName} style={{ display: 'flex', alignItems: 'center', gap: 8, border: '1px solid var(--border)', borderRadius: 12, padding: '10px 12px', background: selected.includes(centerName) ? 'rgba(13, 148, 136, 0.08)' : 'var(--surface-solid)', fontWeight: 700, color: 'var(--text)', cursor: 'pointer' }}>
            <input type="checkbox" checked={selected.includes(centerName)} onChange={() => onToggle(centerName)} />
            <span>{centerName}</span>
          </label>
        ))}
      </div>
      <div style={{ marginTop: 8, color: 'var(--text-muted)', fontSize: 12, fontWeight: 650 }}>
        ALL filters combine only these centers unless the user is Superadmin.
      </div>
    </div>
  );
}

function MultiAccessPicker({ label, options, selected, onChange }: { label: string; options: Array<{ key: string; displayName: string }>; selected: string[]; onChange: (value: string[]) => void }) {
  const allSelected = options.length > 0 && options.every((item) => selected.includes(item.key));
  return <div className="input-group user-modal-wide">
    <UserFormLabel>{label}</UserFormLabel>
    <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, marginBottom: 10, fontSize: 12, fontWeight: 800, color: 'var(--text-muted)', cursor: 'pointer' }}><input type="checkbox" checked={allSelected} onChange={(event) => onChange(event.target.checked ? options.map((item) => item.key) : [])} />Select all</label>
    <div className="user-center-picker-list">{options.map((item) => <label key={item.key} style={{ display: 'flex', alignItems: 'center', gap: 8, border: '1px solid var(--border)', borderRadius: 12, padding: '10px 12px', background: selected.includes(item.key) ? 'rgba(13, 148, 136, 0.08)' : 'var(--surface-solid)', fontWeight: 700, color: 'var(--text)', cursor: 'pointer' }}><input type="checkbox" checked={selected.includes(item.key)} onChange={() => onChange(selected.includes(item.key) ? selected.filter((key) => key !== item.key) : [...selected, item.key])} /><span>{item.displayName} ({item.key})</span></label>)}</div>
    <div style={{ marginTop: 8, color: 'var(--text-muted)', fontSize: 12, fontWeight: 650 }}>Targets are server-restricted to the selected assignments as well as centre access.</div>
  </div>;
}

function UserFormLabel({ children }: { children: React.ReactNode }) {
  return <label style={{ fontFamily: 'Outfit', fontWeight: 800, fontSize: 11.5, textTransform: 'uppercase', color: 'var(--text-muted)', letterSpacing: '0.05em', marginBottom: 8 }}>{children}</label>;
}

function UserStatusEditor({ isActive, reason, onActiveChange, onReasonChange }: { isActive: boolean; reason: string; onActiveChange: (value: boolean) => void; onReasonChange: (value: string) => void }) {
  return (
    <div className="input-group user-status-editor">
      <UserFormLabel>Status</UserFormLabel>
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
        <button type="button" className={isActive ? 'btn btn-primary' : 'btn btn-outline'} style={{ height: 36, borderRadius: 10, padding: '0 14px', fontSize: 12 }} onClick={() => onActiveChange(true)}>Active</button>
        <button type="button" className={!isActive ? 'btn btn-primary' : 'btn btn-outline'} style={{ height: 36, borderRadius: 10, padding: '0 14px', fontSize: 12 }} onClick={() => onActiveChange(false)}>Inactive</button>
      </div>
      {!isActive && (
        <textarea
          value={reason}
          onChange={event => onReasonChange(event.target.value)}
          placeholder="Reason for deactivation"
          style={{ ...userInputStyle, height: 64, resize: 'vertical', marginTop: 10, width: '100%', lineHeight: 1.45 }}
        />
      )}
    </div>
  );
}

function UserTable({ rows, empty, roles, currentUserId, canUpdate, canDelete, onEdit, onDelete, onForceLogout, onUnlockLogin, onResendWelcome }: { rows: AdminUser[]; empty: string; roles: AccessRole[]; currentUserId?: string; canUpdate: boolean; canDelete: boolean; onEdit: (row: AdminUser) => void; onDelete: (row: AdminUser) => void; onForceLogout: (row: AdminUser) => void; onUnlockLogin: (row: AdminUser) => void; onResendWelcome:(row:AdminUser)=>void }) {
  const roleMap = new Map(roles.map(role => [role.key, role]));
  return (
    <div style={{ overflowX: 'auto' }}>
      <table id="user-table" className="report-table" style={{ marginTop: 0, width: '100%', tableLayout: 'fixed' }}>
        <thead>
          <tr>
            <th style={{ width: '30%', fontFamily: 'Outfit', fontWeight: 800, fontSize: 11.5, letterSpacing: '0.05em', color: 'var(--text-muted)', textTransform: 'uppercase' }}>User</th>
            <th style={{ width: '14%', fontFamily: 'Outfit', fontWeight: 800, fontSize: 11.5, letterSpacing: '0.05em', color: 'var(--text-muted)', textTransform: 'uppercase' }}>Role</th>
            <th style={{ width: '22%', fontFamily: 'Outfit', fontWeight: 800, fontSize: 11.5, letterSpacing: '0.05em', color: 'var(--text-muted)', textTransform: 'uppercase' }}>Center</th>
            <th style={{ width: '10%', fontFamily: 'Outfit', fontWeight: 800, fontSize: 11.5, letterSpacing: '0.05em', color: 'var(--text-muted)', textTransform: 'uppercase' }}>Status</th>
            <th style={{ width: '24%', textAlign: 'center', fontFamily: 'Outfit', fontWeight: 800, fontSize: 11.5, letterSpacing: '0.05em', color: 'var(--text-muted)', textTransform: 'uppercase' }}>Action</th>
          </tr>
        </thead>
        <tbody id="user-body">
          {rows.length ? rows.map((row, index) => (
            <tr key={String(row.id ?? index)}>
              <td style={{ padding: 16, verticalAlign: 'middle' }}>
                <div style={{ display: 'grid', gap: 4, minWidth: 0 }}>
                  <strong style={{ color: 'var(--text)', fontWeight: 800, fontFamily: 'Outfit', fontSize: 14.5, display: 'inline-flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                    <span style={{ color: 'var(--primary)', display: 'inline-flex', flexShrink: 0 }}><SmallIcon name="shield" /></span>
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{cell(displayNameFallback(row))}</span>
                  </strong>
                  <span style={{ color: 'var(--text-muted)', fontSize: 12, fontWeight: 750, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{cell(row.username)}</span>
                  <span style={{ color: 'var(--text-muted)', fontSize: 12, fontWeight: 650, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{cell(row.email)}</span>
                  <span style={{ color: 'var(--text-muted)', fontSize: 12, fontWeight: 650, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{cell(row.phone_number)}</span>
                </div>
              </td>
              <td style={{ padding: 16, verticalAlign: 'middle' }}><AccountTypeBadge role={(row.roles && row.roles[0]) || row.role} roleMeta={roleMap.get((row.roles && row.roles[0]) || row.role)} isSuperAdmin={row.is_superadmin} /></td>
              <td style={{ padding: 16, verticalAlign: 'middle' }}><UserCenterBadges centers={(row.centerAccess && row.centerAccess.length ? row.centerAccess : [row.center_name || 'All']).filter(Boolean)} /></td>
              <td style={{ padding: 16, verticalAlign: 'middle' }}><UserStatusBadge active={row.is_active !== false} locked={Boolean(row.login_locked)} lockedUntil={row.login_locked_until} /></td>
              <td style={{ padding: 16, textAlign: 'center', verticalAlign: 'middle' }}><UserRowActions user={row} isSelf={Boolean(currentUserId && String(row.id) === String(currentUserId))} canUpdate={canUpdate} canDelete={canDelete} onEdit={() => onEdit(row)} onDelete={() => onDelete(row)} onForceLogout={() => onForceLogout(row)} onUnlockLogin={() => onUnlockLogin(row)} onResendWelcome={()=>onResendWelcome(row)} /></td>
            </tr>
          )) : <tr><td colSpan={5} style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)' }}>{empty}</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

function UserCenterBadges({ centers }: { centers: string[] }) {
  return <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>{centers.map(centerName => <span key={centerName} className="badge user-center-badge"><span><SmallIcon name="location" /></span>{cell(centerName)}</span>)}</div>;
}

function UserStatusBadge({ active, locked = false, lockedUntil }: { active: boolean; locked?: boolean; lockedUntil?: string | Date | null }) {
  return (
    <div style={{ display: 'grid', justifyItems: 'start', gap: 6 }}>
      <span className="badge" style={{ background: active ? 'rgba(16, 185, 129, 0.1)' : 'rgba(239, 68, 68, 0.1)', color: active ? '#059669' : 'var(--danger)', border: `1px solid ${active ? 'rgba(16, 185, 129, 0.18)' : 'rgba(239, 68, 68, 0.18)'}`, fontWeight: 800, fontSize: 12, display: 'inline-flex', textTransform: 'none' }}>
        {active ? 'Active' : 'Inactive'}
      </span>
      {locked && (
        <span className="badge" title={lockedUntil ? `Locked until ${new Date(lockedUntil).toLocaleString()}` : 'Temporarily login locked'} style={{ background: 'rgba(245, 158, 11, 0.12)', color: '#b45309', border: '1px solid rgba(245, 158, 11, 0.25)', fontWeight: 800, fontSize: 11, display: 'inline-flex', alignItems: 'center', gap: 4, textTransform: 'none', whiteSpace: 'nowrap' }}>
          <SmallIcon name="lock" /> Login locked
        </span>
      )}
    </div>
  );
}

function AccountTypeBadge({ role, roleMeta, isSuperAdmin }: { role?: string; roleMeta?: AccessRole; isSuperAdmin?: boolean }) {
  const isAdmin = role === 'ADMIN' || isSuperAdmin;
  const isSurvey = role === 'SURVEY';
  const badgeStyle = roleMeta?.color_hex
    ? { background: roleMeta.color_rgba || `${roleMeta.color_hex}1f`, color: roleMeta.color_text || roleMeta.color_hex, border: `1px solid ${roleMeta.color_hex}33` }
    : isAdmin
    ? { background: 'rgba(233, 30, 99, 0.08)', color: 'var(--primary)', border: '1px solid rgba(233, 30, 99, 0.15)' }
    : isSurvey
      ? { background: 'rgba(59, 130, 246, 0.08)', color: '#3b82f6', border: '1px solid rgba(59, 130, 246, 0.15)' }
      : { background: 'rgba(13, 148, 136, 0.08)', color: 'var(--accent)', border: '1px solid rgba(13, 148, 136, 0.15)' };
  return <span className="badge" style={{ ...badgeStyle, fontWeight: 700, fontSize: 12, display: 'inline-flex', alignItems: 'center', textTransform: 'none' }}><span style={{ marginRight: 4, display: 'inline-flex' }}><SmallIcon name={isAdmin ? 'lock' : isSurvey ? 'fileEdit' : 'bar'} /></span>{roleLabel(role, isSuperAdmin)}</span>;
}

function UserRowActions({ user, isSelf, canUpdate, canDelete, onEdit, onDelete, onForceLogout, onUnlockLogin, onResendWelcome }: { user: AdminUser; isSelf: boolean; canUpdate: boolean; canDelete: boolean; onEdit: () => void; onDelete: () => void; onForceLogout: () => void; onUnlockLogin: () => void; onResendWelcome:()=>void }) {
  const protectedUser = isProtectedUserForLogout(user);
  const online = Boolean(user.is_online);
  const logoutDisabled = !canUpdate || !online || isSelf || protectedUser;
  const logoutTitle = !canUpdate
    ? 'You do not have permission to update users.'
    : isSelf
      ? 'Users cannot force logout their own account.'
      : protectedUser
        ? 'Superadmin and administrator users cannot be force logged out.'
        : online
          ? 'Force logout this online user.'
          : 'User is currently offline.';
  return (
    <div className="user-row-actions">
      <button className="user-action-btn user-action-edit" disabled={!canUpdate} title={canUpdate ? undefined : 'You do not have permission to edit users.'} onClick={onEdit}><SmallIcon name="edit" /><span>Edit</span></button>
      <button className="user-action-btn" disabled={!canUpdate || !user.email} title={user.email?'Issue a new temporary password and resend the welcome email.':'A registered email is required.'} onClick={onResendWelcome}><SmallIcon name="message" /><span>Resend Welcome</span></button>
      <button className="user-action-btn user-action-delete" disabled={!canDelete || isSelf} title={isSelf ? 'Users cannot delete their own account.' : canDelete ? undefined : 'You do not have permission to delete users.'} onClick={onDelete}><SmallIcon name="trash" /><span>Delete</span></button>
      {user.login_locked && (
        <button
          className="user-action-btn"
          disabled={!canUpdate}
          title={canUpdate ? 'Clear this user\'s temporary failed-login lock.' : 'You do not have permission to update users.'}
          onClick={onUnlockLogin}
          style={{ background: 'rgba(245, 158, 11, 0.12)', color: '#b45309', borderColor: 'rgba(245, 158, 11, 0.28)' }}
        >
          <SmallIcon name="lock" /><span>Unlock Login</span>
        </button>
      )}
      <button
        className="user-action-btn"
        disabled={logoutDisabled}
        title={logoutTitle}
        onClick={onForceLogout}
        style={{
          background: online ? 'rgba(16, 185, 129, 0.12)' : 'rgba(239, 68, 68, 0.1)',
          color: online ? '#047857' : '#dc2626',
          border: `1px solid ${online ? 'rgba(16, 185, 129, 0.28)' : 'rgba(239, 68, 68, 0.22)'}`,
          opacity: logoutDisabled && online ? 0.6 : 1,
        }}
      >
        <SmallIcon name={online ? 'check' : 'close'} />
        <span>{online ? 'User Online' : 'User Offline'}</span>
      </button>
    </div>
  );
}

function BulkLogoutModal({ roles, selected, error, onToggle, onCancel, onConfirm }: { roles: Array<{ key: string; name: string }>; selected: string[]; error: string; onToggle: (roleKey: string) => void; onCancel: () => void; onConfirm: () => void }) {
  return (
    <div className="modal-overlay user-account-modal-overlay" style={{ display: 'flex' }} role="presentation">
      <div className="modal-content admin-premium-modal user-delete-modal" role="dialog" aria-modal="true" aria-labelledby="bulk-logout-title">
        <button onClick={onCancel} aria-label="Close logout users modal" className="admin-modal-close">&times;</button>
        <h3 id="bulk-logout-title" style={{ fontFamily: 'Outfit', fontSize: 22, fontWeight: 850, color: 'var(--text)', marginBottom: 8 }}>Logout Users</h3>
        <p style={{ color: 'var(--text-muted)', fontSize: 13, fontWeight: 650, lineHeight: 1.5, marginBottom: 18 }}>Select roles to force logout all currently online users in those roles. Superadmin and administrator roles are protected.</p>
        {error && <div className="user-modal-error" role="alert">{error}</div>}
        <div style={{ display: 'grid', gap: 10, maxHeight: 320, overflow: 'auto', paddingRight: 4 }}>
          {roles.length ? roles.map(role => (
            <label key={role.key} style={{ display: 'flex', alignItems: 'center', gap: 10, border: '1px solid var(--border)', borderRadius: 14, padding: '12px 14px', background: selected.includes(role.key) ? 'rgba(233, 30, 99, 0.08)' : 'var(--surface-solid)', fontWeight: 800, color: 'var(--text)', cursor: 'pointer' }}>
              <input type="checkbox" checked={selected.includes(role.key)} onChange={() => onToggle(role.key)} />
              <span>{role.name}</span>
              <span style={{ marginLeft: 'auto', color: 'var(--text-muted)', fontSize: 11, fontWeight: 800 }}>{role.key}</span>
            </label>
          )) : <p style={{ color: 'var(--text-muted)', fontWeight: 700 }}>No eligible roles are available.</p>}
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 20, flexWrap: 'wrap' }}>
          <button className="btn btn-outline" onClick={onCancel}>Cancel</button>
          <button className="btn btn-primary" disabled={!selected.length} onClick={onConfirm}>User Logout</button>
        </div>
      </div>
    </div>
  );
}

function ForceLogoutUserModal({ user, error, onCancel, onConfirm }: { user: AdminUser; error: string; onCancel: () => void; onConfirm: () => void }) {
  return (
    <div className="modal-overlay user-account-modal-overlay" style={{ display: 'flex' }} role="presentation">
      <div className="modal-content admin-premium-modal user-delete-modal" role="dialog" aria-modal="true" aria-labelledby="force-logout-user-title">
        <button onClick={onCancel} aria-label="Close force logout confirmation" className="admin-modal-close">&times;</button>
        <h3 id="force-logout-user-title" style={{ fontFamily: 'Outfit', fontSize: 22, fontWeight: 850, color: 'var(--text)', marginBottom: 8 }}>Logout User</h3>
        <p style={{ color: 'var(--text-muted)', fontSize: 13, fontWeight: 650, lineHeight: 1.5, marginBottom: 18 }}>This will invalidate the current session for <strong style={{ color: 'var(--text)' }}>{displayNameFallback(user)}</strong>. The user will be redirected to login on their next request.</p>
        {error && <div className="user-modal-error" role="alert">{error}</div>}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 20, flexWrap: 'wrap' }}>
          <button className="btn btn-outline" onClick={onCancel}>Cancel</button>
          <button className="btn btn-primary" onClick={onConfirm}>Confirmed</button>
        </div>
      </div>
    </div>
  );
}

function UnlockLoginUserModal({ user, error, onCancel, onConfirm }: { user: AdminUser; error: string; onCancel: () => void; onConfirm: () => void }) {
  return (
    <div className="modal-overlay user-account-modal-overlay" style={{ display: 'flex' }} role="presentation">
      <div className="modal-content admin-premium-modal user-delete-modal" role="dialog" aria-modal="true" aria-labelledby="unlock-login-user-title">
        <button onClick={onCancel} aria-label="Close unlock login confirmation" className="admin-modal-close">&times;</button>
        <div style={{ color: '#b45309', marginBottom: 12 }}><SmallIcon name="lock" /></div>
        <h3 id="unlock-login-user-title" style={{ fontFamily: 'Outfit', fontSize: 22, fontWeight: 850, color: 'var(--text)', marginBottom: 8 }}>Unlock User Login</h3>
        <p style={{ color: 'var(--text-muted)', fontSize: 13, fontWeight: 650, lineHeight: 1.5, marginBottom: 18 }}>
          Clear all temporary failed-login attempts for <strong style={{ color: 'var(--text)' }}>{displayNameFallback(user)}</strong>? This does not change the password, account status, or active sessions.
        </p>
        {error && <div className="user-modal-error" role="alert">{error}</div>}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 20, flexWrap: 'wrap' }}>
          <button className="btn btn-outline" onClick={onCancel}>Cancel</button>
          <button className="btn btn-primary" onClick={onConfirm}>Unlock Login</button>
        </div>
      </div>
    </div>
  );
}

function DeleteUserModal({ user, error, onCancel, onConfirm }: { user: AdminUser; error: string; onCancel: () => void; onConfirm: () => void }) {
  const [secondsLeft, setSecondsLeft] = useState(10);
  useEffect(() => {
    if (secondsLeft <= 0) return undefined;
    const timer = window.setTimeout(() => setSecondsLeft(value => Math.max(0, value - 1)), 1000);
    return () => window.clearTimeout(timer);
  }, [secondsLeft]);
  const display = displayNameFallback(user);
  return (
    <div className="modal-overlay user-account-modal-overlay" style={{ display: 'flex' }} role="presentation">
      <div className="modal-content admin-premium-modal user-delete-modal" role="dialog" aria-modal="true" aria-labelledby="delete-user-title">
        <button onClick={onCancel} aria-label="Close delete confirmation" className="admin-modal-close">&times;</button>
        <div className="user-delete-icon"><SmallIcon name="trash" /></div>
        <h3 id="delete-user-title">Delete User Account</h3>
        <p>
          This will permanently delete <strong>{display}</strong> ({user.username}). This action should only be used when the account must be removed completely.
        </p>
        {error && <div className="user-modal-error" role="alert">{error}</div>}
        <div className="user-delete-summary">
          <span>Role: {roleLabel((user.roles && user.roles[0]) || user.role, user.is_superadmin)}</span>
          <span>Centers: {(user.centerAccess && user.centerAccess.length ? user.centerAccess : [user.center_name || 'All']).join(', ')}</span>
        </div>
        <div className="user-delete-actions">
          <button type="button" className="btn btn-outline" onClick={onCancel}>Cancel</button>
          <button
            type="button"
            className="btn btn-primary user-delete-confirm"
            disabled={secondsLeft > 0}
            onClick={onConfirm}
          >
            {secondsLeft > 0 ? `Delete available in ${secondsLeft}s` : 'Delete User'}
          </button>
        </div>
      </div>
    </div>
  );
}

function DeleteStaffModal({ staff, error, onCancel, onConfirm }: { staff: TeamMember; error: string; onCancel: () => void; onConfirm: () => void }) {
  const [secondsLeft, setSecondsLeft] = useState(10);
  useEffect(() => {
    if (secondsLeft <= 0) return undefined;
    const timer = window.setTimeout(() => setSecondsLeft(value => Math.max(0, value - 1)), 1000);
    return () => window.clearTimeout(timer);
  }, [secondsLeft]);
  return (
    <div className="modal-overlay user-account-modal-overlay" style={{ display: 'flex' }} role="presentation">
      <div className="modal-content admin-premium-modal user-delete-modal" role="dialog" aria-modal="true" aria-labelledby="delete-staff-title">
        <button onClick={onCancel} aria-label="Close delete confirmation" className="admin-modal-close">&times;</button>
        <div className="user-delete-icon"><SmallIcon name="trash" /></div>
        <h3 id="delete-staff-title">Delete Staff Member</h3>
        <p>
          This will permanently delete <strong>{cell(staff.name)}</strong>. If this staff member has linked feedback, the backend will block deletion and the member should be marked inactive instead.
        </p>
        {error && <div className="user-modal-error" role="alert">{error}</div>}
        <div className="user-delete-summary">
          <span>Center: {cell(staff.center_name ?? staff.center)}</span>
          <span>Status: {staff.is_active === false ? 'Inactive' : 'Active'}</span>
        </div>
        <div className="user-delete-actions">
          <button type="button" className="btn btn-outline" onClick={onCancel}>Cancel</button>
          <button type="button" className="btn btn-primary user-delete-confirm" disabled={secondsLeft > 0} onClick={onConfirm}>
            {secondsLeft > 0 ? `Delete available in ${secondsLeft}s` : 'Delete Staff'}
          </button>
        </div>
      </div>
    </div>
  );
}

function displayNameCandidates(firstName: string, middleName: string, lastName: string, username: string): string[] {
  const first = firstName.trim();
  const middle = middleName.trim();
  const last = lastName.trim();
  const candidates = [
    [first, last].filter(Boolean).join(' '),
    [last, first].filter(Boolean).join(' '),
    [first, middle, last].filter(Boolean).join(' '),
    username.trim(),
  ].filter(Boolean);
  return [...new Set(candidates)].length ? [...new Set(candidates)] : ['User'];
}

function displayNameFallback(user: AdminUser): string {
  const explicit = user.display_name?.trim();
  if (explicit) return explicit;
  const fullName = [user.first_name, user.middle_name, user.last_name].map(part => part?.trim()).filter(Boolean).join(' ');
  return fullName || user.username;
}

export function SurveyQuestionsTab({
  active,
  rows,
  actions,
  canCreate = true,
  canUpdate = true,
  canDelete = true,
}: {
  active: boolean;
  rows: SurveyQuestion[];
  actions: AdminActions;
  canCreate?: boolean;
  canUpdate?: boolean;
  canDelete?: boolean;
}) {
  const questionFormRef = useRef<HTMLDivElement | null>(null);
  const [editing, setEditing] = useState<SurveyQuestion | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [flowOpen, setFlowOpen] = useState(true);
  const [filterJourney, setFilterJourney] = useState('ALL');
  const [questionText, setQuestionText] = useState('');
  const [category, setCategory] = useState('Overall Experience');
  const [journey, setJourney] = useState('NEW_PATIENT');
  const [questionType, setQuestionType] = useState('star');
  const [displayOrder, setDisplayOrder] = useState(4);
  const filtered = rows.filter(row => filterJourney === 'ALL' || row.journey === filterJourney);
  const openQuestionForm = () => {
    if (!canCreate) {
      actions.notify('You do not have permission to create survey questions.');
      return;
    }
    setEditing(null);
    setQuestionText('');
    setJourney('NEW_PATIENT');
    setQuestionType('star');
    setCategory('Overall Experience');
    setDisplayOrder(4);
    setFormOpen(prev => !prev);
  };
  const editQuestion = (question: SurveyQuestion) => {
    if (!canUpdate) {
      actions.notify('You do not have permission to edit survey questions.');
      return;
    }
    setEditing(question);
    setQuestionText(question.question_text);
    setJourney(String(question.journey || 'NEW_PATIENT'));
    setQuestionType(question.question_type || 'star');
    setCategory(question.category || '');
    setDisplayOrder(Number(question.display_order || 0));
    setFormOpen(true);
    window.setTimeout(() => questionFormRef.current?.scrollIntoView({ behavior: 'smooth' }), 0);
  };
  const submit = async () => {
    if (!questionText.trim()) {
      actions.notify('Text required');
      return;
    }
    const payload = { question_text: questionText.trim(), category, journey, question_type: questionType, display_order: Number(displayOrder) || 0 };
    if (editing?.id) {
      if (!canUpdate) {
        actions.notify('You do not have permission to edit survey questions.');
        return;
      }
      await actions.updateQuestion(editing.id, payload);
    }
    else {
      if (!canCreate) {
        actions.notify('You do not have permission to create survey questions.');
        return;
      }
      await actions.createQuestion(payload);
    }
    setEditing(null);
    setQuestionText('');
    setFormOpen(false);
  };
  return <section id="tab-questions" className="tab-content" style={{ display: active ? 'block' : 'none' }}><div className="card"><div style={{ display: 'flex', flexDirection: 'column', alignItems: 'stretch', gap: 16, marginBottom: 32 }}><h2 className="section-title" style={{ margin: 0, fontFamily: 'Outfit', fontSize: 28, fontWeight: 800, color: 'var(--text)' }}>Question Management</h2><div style={{ display: 'flex', gap: 12, alignItems: 'center', justifyContent: 'flex-end', width: '100%' }}><div style={{ width: 220 }}><ChoicesSelect id="filter-question-journey" value={filterJourney} onChange={setFilterJourney} searchEnabled={false} options={[{ label: 'All Journeys', value: 'ALL' }, { label: 'New Patient', value: 'NEW_PATIENT' }, { label: 'OPU', value: 'OPU' }, { label: 'Day of ET', value: 'ET' }]} /></div><button className="btn btn-primary" style={{ padding: '10px 20px', fontSize: 13 }} onClick={openQuestionForm} disabled={!canCreate} title={!canCreate ? 'You do not have permission to create survey questions.' : undefined}>+ Add Question</button></div></div>{formOpen && <div ref={questionFormRef} id="question-form" className="card" style={{ borderLeft: '4px solid var(--primary)', paddingLeft: 32, marginBottom: 32 }}><h3 id="question-form-title" style={{ marginBottom: 24 }}>{editing ? 'Edit Question' : 'Add Question'}</h3><input type="hidden" id="q-id" value={editing?.id ?? ''} readOnly /><div className="form-grid-2"><div className="input-group" style={{ gridColumn: 'span 2' }}><label>Question Text</label><input type="text" id="q-text" value={questionText} onChange={event => setQuestionText(event.target.value)} placeholder="e.g. How satisfied were you with the nursing care?" /></div><div className="input-group"><label>Journey</label><ChoicesSelect id="q-journey" value={journey} onChange={setJourney} searchEnabled={false} options={[{ label: 'New Patient', value: 'NEW_PATIENT' }, { label: 'OPU', value: 'OPU' }, { label: 'Day of ET', value: 'ET' }]} /></div><div className="input-group"><label>Question Type</label><ChoicesSelect id="q-type" value={questionType} onChange={setQuestionType} searchEnabled={false} options={[{ label: 'Star Rating (1-5)', value: 'star' }, { label: 'Yes/No Toggle', value: 'toggle' }, { label: 'Open Text Area', value: 'text' }]} /></div><div className="input-group"><label>Category (Section)</label><CategorySelect value={category} onChange={setCategory} /></div><div className="input-group"><label>Section Number (Order)</label><input id="q-order" type="number" value={displayOrder} onChange={event => setDisplayOrder(Number(event.target.value))} /></div></div><div style={{ display: 'flex', gap: 12, marginTop: 20 }}><button className="btn btn-primary" onClick={() => void submit()} style={{ flex: 2 }}>{editing ? 'Save Question' : 'Create Question'}</button><button className="btn btn-outline" onClick={() => setFormOpen(false)} style={{ flex: 1 }}>Cancel</button></div></div>}<SurveyFlow journeyFilter={filterJourney} open={flowOpen} onToggle={() => setFlowOpen(prev => !prev)} onJourney={setFilterJourney} /><QuestionTable rows={filtered} onEdit={editQuestion} onDelete={id => { if (!canDelete) { actions.notify('You do not have permission to delete survey questions.'); return; } void actions.deleteQuestion(id); }} canUpdate={canUpdate} canDelete={canDelete} /></div></section>;
}

function CategorySelect({ value, onChange }: { value: string; onChange: (value: string) => void }) {
  return <ChoicesSelect id="q-cat" value={value} onChange={onChange} searchEnabled options={[{ label: 'Global / Standard', options: [{ label: 'Overall Experience', value: 'Overall Experience' }, { label: 'Appreciation', value: 'Appreciation' }, { label: 'General Feedback', value: 'General Feedback' }] }, { label: 'New Patient Journey', options: [{ label: 'Registration Process', value: 'Registration Process' }, { label: 'Appointment Time', value: 'Appointment Time' }, { label: 'Doctor NPS', value: 'Doctor NPS' }, { label: 'Pharmacy Service Usage', value: 'Pharmacy Service Usage' }, { label: 'Pharmacy Services', value: 'Pharmacy Services' }, { label: 'Pathology Usage', value: 'Pathology Usage' }, { label: 'Efficiency and Professionalism', value: 'Efficiency and Professionalism' }, { label: 'Assistance (Staff Assistance)', value: 'Assistance' }, { label: 'Staff Name', value: 'Staff Name' }, { label: 'Suggestions', value: 'Suggestions' }] }, { label: 'Day of OPU Journey', options: [{ label: 'Admission Process', value: 'Admission Process' }, { label: 'Staff Helpfulness', value: 'Staff Helpfulness' }, { label: 'Nursing Services', value: 'Nursing Services' }, { label: 'Psychological Counselling Usage', value: 'Psychological Counselling Usage' }, { label: 'Psychological Counselling', value: 'Psychological Counselling' }, { label: 'OPU Care and Monitoring', value: 'OPU Care and Monitoring' }, { label: 'Discharge Process', value: 'Discharge Process' }, { label: 'Staff Assistance OPU', value: 'Staff Assistance OPU' }, { label: 'Staff Name OPU', value: 'Staff Name OPU' }, { label: 'Suggestions OPU', value: 'Suggestions OPU' }] }, { label: 'Day of ET Journey', options: [{ label: 'Punctuality and Timeliness', value: 'Punctuality and Timeliness' }, { label: 'Procedure Explaination', value: 'Procedure Explaination' }, { label: 'Recovery Services and Monitoring', value: 'Recovery Services and Monitoring' }, { label: 'Staff Assistance ET', value: 'Staff Assistance ET' }, { label: 'Staff Name ET', value: 'Staff Name ET' }, { label: 'Suggestions ET', value: 'Suggestions ET' }] }]} />;
}

function SurveyFlow({ journeyFilter, open, onToggle, onJourney }: { journeyFilter: string; open: boolean; onToggle: () => void; onJourney: (value: string) => void }) {
  return <div id="survey-flow-container" className="card" style={{ marginTop: 32, marginBottom: 32, background: 'var(--bg)', border: '1px solid var(--border)', padding: open ? 24 : '24px 24px 16px', boxShadow: 'var(--shadow-sm)' }}><div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20, flexWrap: 'wrap', gap: 12 }}><div style={{ display: 'flex', alignItems: 'center', gap: 12 }}><span style={{ fontSize: 24 }}>🗺️</span><div><h3 style={{ margin: 0, fontFamily: 'Outfit, sans-serif', fontSize: 16, fontWeight: 800, color: 'var(--text)' }}>Dynamic Survey Flow Map</h3><p style={{ margin: 0, fontSize: 12, color: 'var(--text-muted)' }}>Visualizes branching triggers and conditional skip logic</p></div></div><button className="btn btn-outline" style={{ padding: '6px 12px', fontSize: 12, height: 'auto' }} onClick={onToggle}>Toggle Map</button></div>{open && <div id="flow-visualizer-body" style={{ transition: 'all 0.3s ease' }}>{journeyFilter === 'ALL' ? <FlowSummaryCards onJourney={onJourney} /> : <DetailedFlow journey={journeyFilter} />}</div>}</div>;
}

function FlowSummaryCards({ onJourney }: { onJourney: (value: string) => void }) {
  const cards = [
    { journey: 'NEW_PATIENT', title: '🌸 New Patient Flow', color: 'var(--primary)', steps: 8, triggers: 3, skips: 3, button: undefined },
    { journey: 'OPU', title: '🧪 Day of OPU Flow', color: 'var(--accent)', steps: 9, triggers: 2, skips: 2, button: 'linear-gradient(135deg, var(--accent), #0b7a70)' },
    { journey: 'ET', title: '👶 Day of ET Flow', color: '#3b82f6', steps: 8, triggers: 2, skips: 2, button: 'linear-gradient(135deg, #3b82f6, #1d4ed8)' },
  ];
  return <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 24, padding: '10px 0' }}>{cards.map(card => <div key={card.journey} style={{ padding: 24, background: 'var(--surface-solid)', borderRadius: 'var(--radius)', border: '1px solid var(--border)', boxShadow: 'var(--shadow-sm)', borderTop: `4px solid ${card.color}`, transition: 'all 0.3s' }} className="flow-summary-card"><h4 style={{ margin: '0 0 12px 0', color: card.color, fontFamily: 'Outfit', fontSize: 16, fontWeight: 800 }}>{card.title}</h4><ul style={{ paddingLeft: 18, fontSize: 13, lineHeight: 1.8, color: 'var(--text-muted)', marginBottom: 16 }}><li><strong>{card.steps}</strong> Total Steps</li><li><strong>{card.triggers}</strong> Branching Triggers</li><li><strong>{card.skips}</strong> Conditional Skips</li></ul><button className="btn btn-primary" style={{ width: '100%', padding: '8px 16px', fontSize: 12, height: 38, textTransform: 'none', borderRadius: 10, background: card.button }} onClick={() => onJourney(card.journey)}>View Detailed Flow</button></div>)}</div>;
}

type FlowStep = { name: string; cat: string; type: 'Linear' | 'Trigger' | 'Conditional' };

function DetailedFlow({ journey }: { journey: string }) {
  const steps = surveyFlowSteps(journey);
  return <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'flex-start', gap: 12, padding: '10px 0' }}>{steps.map((step, index) => <div key={`${step.cat}-${index}`} style={{ display: 'contents' }}><FlowStepCard step={step} index={index} />{index < steps.length - 1 && <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-light)', transition: 'all 0.3s ease' }}><SmallIcon name="chevronRight" /></div>}</div>)}</div>;
}

function FlowStepCard({ step, index }: { step: FlowStep; index: number }) {
  const trigger = step.type === 'Trigger';
  const conditional = step.type === 'Conditional';
  const indicatorColor = trigger ? '#0d9488' : conditional ? 'var(--primary)' : 'var(--text-light)';
  const typeText = trigger ? '⚡ Trigger Branch' : conditional ? '🔗 Conditional Skip' : '📝 Linear Step';
  return <div style={{ flex: 1, minWidth: 140, maxWidth: 170, padding: '16px 12px', background: trigger ? 'rgba(13, 148, 136, 0.03)' : conditional ? 'rgba(233, 30, 99, 0.03)' : 'white', border: `1px solid ${trigger ? 'rgba(13, 148, 136, 0.2)' : conditional ? 'rgba(233, 30, 99, 0.2)' : 'var(--border)'}`, borderTop: `4px solid ${indicatorColor}`, borderRadius: 'var(--radius)', textAlign: 'center', boxShadow: 'var(--shadow-sm)', position: 'relative', transition: 'all 0.3s ease', cursor: 'help' }} className="flow-step-card" title={`${step.cat}: ${step.type} Question`}><div style={{ fontSize: 11, fontWeight: 800, color: 'var(--text-light)', textTransform: 'uppercase', marginBottom: 4, letterSpacing: '0.05em' }}>Step {index + 1}</div><div style={{ fontWeight: 800, color: 'var(--text)', fontSize: 13, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', marginBottom: 4 }} title={step.cat}>{step.name}</div><div style={{ fontSize: 10, color: indicatorColor, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.02em' }}>{typeText}</div>{trigger && <div style={{ position: 'absolute', right: -8, top: '50%', transform: 'translateY(-50%)', background: '#0d9488', color: 'white', width: 18, height: 18, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 800, zIndex: 5, boxShadow: '0 4px 8px rgba(13, 148, 136, 0.3)' }} title="Triggers branch if Yes">Y</div>}</div>;
}

function surveyFlowSteps(journey: string): FlowStep[] {
  if (journey === 'NEW_PATIENT') return [{ name: 'Registration', cat: 'Registration Process', type: 'Linear' }, { name: 'Appt Time', cat: 'Appointment Time', type: 'Linear' }, { name: 'Doctor care', cat: 'Doctor NPS', type: 'Linear' }, { name: 'Pharmacy ?', cat: 'Pharmacy Service Usage', type: 'Trigger' }, { name: 'Pharmacy NPS', cat: 'Pharmacy Services', type: 'Conditional' }, { name: 'Pathology ?', cat: 'Pathology Usage', type: 'Trigger' }, { name: 'Pathology NPS', cat: 'Efficiency and Professionalism', type: 'Conditional' }, { name: 'Assistance ?', cat: 'Assistance', type: 'Trigger' }, { name: 'Staff Name', cat: 'Staff Name', type: 'Conditional' }, { name: 'Appreciation', cat: 'Appreciation', type: 'Linear' }, { name: 'Suggestions', cat: 'Suggestions', type: 'Linear' }];
  if (journey === 'OPU') return [{ name: 'Admission', cat: 'Admission Process', type: 'Linear' }, { name: 'Staff Help', cat: 'Staff Helpfulness', type: 'Linear' }, { name: 'Nursing', cat: 'Nursing Services', type: 'Linear' }, { name: 'Counselling ?', cat: 'Psychological Counselling Usage', type: 'Trigger' }, { name: 'Counselling NPS', cat: 'Psychological Counselling', type: 'Conditional' }, { name: 'OPU Care', cat: 'OPU Care and Monitoring', type: 'Linear' }, { name: 'Discharge', cat: 'Discharge Process', type: 'Linear' }, { name: 'Assistance ?', cat: 'Staff Assistance OPU', type: 'Trigger' }, { name: 'Staff Name', cat: 'Staff Name OPU', type: 'Conditional' }, { name: 'Appreciation', cat: 'Appreciation', type: 'Linear' }, { name: 'Suggestions', cat: 'Suggestions OPU', type: 'Linear' }];
  if (journey === 'ET') return [{ name: 'Punctuality', cat: 'Punctuality and Timeliness', type: 'Linear' }, { name: 'Explanation', cat: 'Procedure Explaination', type: 'Linear' }, { name: 'Recovery', cat: 'Recovery Services and Monitoring', type: 'Linear' }, { name: 'Counselling ?', cat: 'Psychological Counselling Usage', type: 'Trigger' }, { name: 'Counselling NPS', cat: 'Psychological Counselling', type: 'Conditional' }, { name: 'Discharge', cat: 'Discharge Process', type: 'Linear' }, { name: 'Assistance ?', cat: 'Staff Assistance ET', type: 'Trigger' }, { name: 'Staff Name', cat: 'Staff Name ET', type: 'Conditional' }, { name: 'Appreciation', cat: 'Appreciation', type: 'Linear' }, { name: 'Suggestions ET', cat: 'Suggestions ET', type: 'Linear' }];
  return [];
}

function QuestionTable({ rows, onEdit, onDelete, canUpdate, canDelete }: { rows: SurveyQuestion[]; onEdit: (row: SurveyQuestion) => void; onDelete: (id: number | string) => void; canUpdate: boolean; canDelete: boolean }) {
  return <div style={{ overflowX: 'auto' }}><table id="question-table" className="report-table" style={{ marginTop: 0, width: '100%' }}><thead><tr><th style={{ width: 60 }}>Order</th><th>Question</th><th>Category</th><th>Journey</th><th>Type</th><th>Flow & Skip Logic</th><th>Actions</th></tr></thead><tbody id="question-body">{rows.length ? rows.map((row, index) => <tr key={String(row.id ?? index)}><td style={{ textAlign: 'center' }}>{row.display_order || 0}</td><td>{row.question_text}</td><td style={{ textAlign: 'center' }}>{row.category || '-'}</td><td style={{ textAlign: 'center' }}><span className="badge" style={{ background: '#f1f5f9' }}>{row.journey}</span></td><td style={{ textAlign: 'center' }}><span className="badge" style={{ background: '#eee' }}>{row.question_type}</span></td><td style={{ padding: '12px 16px' }}><QuestionFlowInfo question={row} /></td><td><div style={{ display: 'flex', gap: 8, justifyContent: 'center' }}><button className="btn btn-outline" style={{ fontSize: 11 }} disabled={!canUpdate} title={!canUpdate ? 'You do not have permission to edit survey questions.' : undefined} onClick={() => onEdit(row)}>Edit</button><button className="btn btn-outline" style={{ fontSize: 11, color: 'var(--danger)' }} disabled={!canDelete} title={!canDelete ? 'You do not have permission to delete survey questions.' : undefined} onClick={() => row.id && onDelete(row.id)}>Delete</button></div></td></tr>) : <tr><td colSpan={7} style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)' }}>No questions found.</td></tr>}</tbody></table></div>;
}

function QuestionFlowInfo({ question }: { question: SurveyQuestion }) {
  const info = questionFlowInfo(question);
  return <div className="flow-info-card" style={{ display: 'flex', flexDirection: 'column', gap: 4, padding: '10px 14px', borderRadius: 12, borderLeft: `4px solid ${info.border}`, background: info.background, boxShadow: '0 2px 8px rgba(0,0,0,0.02)', minWidth: 220, transition: 'all 0.2s ease-in-out' }}><div style={{ fontSize: 11.5, fontWeight: 800, color: info.color, display: 'flex', alignItems: 'center', gap: 4, letterSpacing: '0.03em', textTransform: 'uppercase' }}><SmallIcon name={info.icon} /><span>{info.title}</span></div><div style={{ fontSize: 11.5, fontWeight: 600, color: 'var(--text-muted)', lineHeight: 1.4 }}>{info.desc}</div></div>;
}

function questionFlowInfo(question: SurveyQuestion): { title: string; desc: string; icon: 'zap' | 'link' | 'arrowDown'; border: string; background: string; color: string } {
  const journey = question.journey || '';
  const cat = question.category || '';
  let result: { title: string; desc: string; icon: 'zap' | 'link' | 'arrowDown'; border: string; background: string; color: string } = { title: 'Linear Flow', desc: 'Sequential question path', icon: 'arrowDown', border: 'var(--text-light)', background: 'rgba(148, 163, 184, 0.06)', color: 'var(--text-muted)' };
  const trigger = (desc: string) => { result = { title: 'Branch Trigger', desc, icon: 'zap', border: '#0d9488', background: 'rgba(13, 148, 136, 0.04)', color: '#0d9488' }; };
  const conditional = (desc: string) => { result = { title: 'Conditional Skip', desc, icon: 'link', border: 'var(--primary)', background: 'rgba(233, 30, 99, 0.04)', color: 'var(--primary)' }; };
  if (journey === 'NEW_PATIENT') {
    if (cat === 'Pharmacy Service Usage') trigger("If 'Yes' -> Show 'Pharmacy Services'");
    else if (cat === 'Pharmacy Services') conditional("Shown only if 'Pharmacy Service Usage' is 'Yes'");
    else if (cat === 'Pathology Usage') trigger("If 'Yes' -> Show 'Pathology Services'");
    else if (cat === 'Efficiency and Professionalism') conditional("Only if 'Pathology Usage' is 'Yes'");
    else if (cat === 'Assistance') trigger("If 'Yes' -> Show 'Staff Name'");
    else if (cat === 'Staff Name') conditional("Only if 'Assistance' is 'Yes'");
  }
  if (journey === 'OPU') {
    if (cat === 'Psychological Counselling Usage') trigger("If 'Yes' -> Show 'Psychological Counselling'");
    else if (cat === 'Psychological Counselling') conditional("Only if 'Psychological Counselling Usage' is 'Yes'");
    else if (cat === 'Staff Assistance OPU') trigger("If 'Yes' -> Show 'Staff Name OPU'");
    else if (cat === 'Staff Name OPU') conditional("Only if 'Staff Assistance OPU' is 'Yes'");
  }
  if (journey === 'ET') {
    if (cat === 'Psychological Counselling Usage') trigger("If 'Yes' -> Show 'Psychological Counselling'");
    else if (cat === 'Psychological Counselling') conditional("Only if 'Psychological Counselling Usage' is 'Yes'");
    else if (cat === 'Staff Assistance ET') trigger("If 'Yes' -> Show 'Staff Name ET'");
    else if (cat === 'Staff Name ET') conditional("Only if 'Staff Assistance ET' is 'Yes'");
  }
  return result;
}


